import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import PageLayout from '../../components/PageLayout';
import { useAuth } from '../../context/AuthContext';
import { db, storage } from '../../config/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { checkEntityReferences } from '../../utils/deletionSafeguard';
import { invalidateMaster, upsertLocalMaster, clearCategoryCaches } from '../../utils/masterDataStore';
import { isItemCodeDuplicate, writeAuditLog } from '../../utils/validationHelpers';
import { generateNextBarcode } from '../../utils/barcodeHelper';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  where,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';
import {
  FiPlus, FiEdit2, FiTrash2, FiChevronDown, FiChevronRight,
  FiFolder, FiPackage, FiBox, FiSearch, FiX, FiDownload, FiUpload, FiImage, FiPrinter,
  FiLayers, FiGrid, FiMoreVertical, FiBookOpen, FiTrendingUp, FiCamera
} from 'react-icons/fi';
import { createPortal } from 'react-dom';
import POSCameraScanner from '../pos/POSCameraScanner';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useEscapeClose from '../../hooks/useEscapeClose';
import { loadDocumentFormats } from '../../utils/printTemplates';
import { printItemCatalogue, CATALOGUE_COLUMNS, CATALOGUE_DESIGNS } from '../../utils/itemCataloguePrint';
import { getTwoUnitPair, fromBaseQty } from '../../utils/unitConversion';
import { isVendorPricingOn, VENDOR_PRICING_COLLECTION } from '../../utils/vendorPricingHelper';
// Live stock scan removed 2026-07-16 (Firebase cost): loadItemStocks read the
// full-FY stockFlow on every page open. The stock column now shows the
// denormalized `currentStock` snapshot already present on each item doc
// (zero extra reads); exact live stock lives in the inventory reports.
// Opening stock is entered via Opening Balances page, not here

const CATALOGUE_COL_DEFAULTS = { image: true, itemCode: true, itemName: true, unitOfMeasure: true, salePrice: true };
const defaultColumnOn = (key) => !!CATALOGUE_COL_DEFAULTS[key];

const ChartOfInventory = () => {
  const { activeCompany, userProfile, isModuleEnabled } = useAuth();
  const { canEdit, canDelete, canChange } = usePagePermissions('masterData', 'items');
  const taxLabel = activeCompany?.taxLabel || 'GST';
  const multiUnitEnabled = isModuleEnabled('master_units');
  // Show Two Units (2026-10-10): item picks two units shown together on lines.
  const twoUnitsEnabled = multiUnitEnabled && isModuleEnabled('item_two_units');
  const thirdScheduleEnabled = isModuleEnabled('sales_third_schedule_tax');
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [unitsBank, setUnitsBank] = useState([]);
  // Live on-hand stock keyed by itemId, computed from stockFlow so the Stock
  // chip on each item always matches Reports → Inventory Balances.
  const [loading, setLoading] = useState(true);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedSubCategories, setExpandedSubCategories] = useState({});

  // Simple (flat) view state
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('coi_viewMode') || 'tree'; } catch { return 'tree'; }
  });
  const [selectedSimpleCategoryId, setSelectedSimpleCategoryId] = useState('all');
  const [selectedSimpleSubCategoryId, setSelectedSimpleSubCategoryId] = useState('all');
  const switchViewMode = (m) => {
    setViewMode(m);
    try { localStorage.setItem('coi_viewMode', m); } catch {}
  };

  // Actions menu (portal-based)
  const navigate = useNavigate();
  const [openActionMenuId, setOpenActionMenuId] = useState(null);
  const [actionMenuPos, setActionMenuPos] = useState({ top: 0, left: 0 });
  const closeActionMenu = () => setOpenActionMenuId(null);
  const toggleActionMenu = (itemId, e) => {
    if (openActionMenuId === itemId) { closeActionMenu(); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuWidth = 220;
    const left = Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8);
    const top = rect.bottom + 4;
    setActionMenuPos({ top, left: Math.max(8, left) });
    setOpenActionMenuId(itemId);
  };
  useEffect(() => {
    if (!openActionMenuId) return;
    const close = (e) => {
      if (!e.target.closest('[data-action-menu]') && !e.target.closest('[data-action-trigger]')) closeActionMenu();
    };
    const onScroll = () => closeActionMenu();
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [openActionMenuId]);

  const handleOpenInventoryLedger = (item) => {
    closeActionMenu();
    navigate('/reports/inventory-ledger', { state: { itemId: item.id } });
  };
  const handleOpenItemPL = (item) => {
    closeActionMenu();
    navigate('/reports/item-profit-loss', { state: { itemId: item.id } });
  };
  const [searchTerm, setSearchTerm] = useState('');

  // Modal states
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  useEscapeClose(showCategoryModal, () => setShowCategoryModal(false));
  const [showSubCategoryModal, setShowSubCategoryModal] = useState(false);
  useEscapeClose(showSubCategoryModal, () => setShowSubCategoryModal(false));
  const [showItemModal, setShowItemModal] = useState(false);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);   // camera scan into the barcode field
  useEscapeClose(showItemModal, () => setShowItemModal(false));
  const [itemModalTab, setItemModalTab] = useState('basic');
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubCategory, setEditingSubCategory] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0, status: '' });
  const [showImportModal, setShowImportModal] = useState(false);
  useEscapeClose(showImportModal, () => setShowImportModal(false));
  const [importResults, setImportResults] = useState(null);
  const fileInputRef = useRef(null);
  // Bulk item-image import — a folder of files named by Item Code (e.g. ITM-001.jpg).
  const imagesInputRef = useRef(null);
  const [importingImages, setImportingImages] = useState(false);
  const [imageImportProgress, setImageImportProgress] = useState({ current: 0, total: 0 });
  const [imageImportResult, setImageImportResult] = useState(null); // { attached, notMatched:[], tooLarge:[], failed:[] }

  // Catalogue print state
  const [showCatalogueModal, setShowCatalogueModal] = useState(false);
  useEscapeClose(showCatalogueModal, () => setShowCatalogueModal(false));
  const [printConfig, setPrintConfig] = useState(null);
  const [catalogueOptions, setCatalogueOptions] = useState({
    design: 'grid',
    categoryFilter: 'all',
    subCategoryFilter: 'all',
    columns: {},
  });

  // Form data
  const [categoryForm, setCategoryForm] = useState({ categoryCode: '', categoryName: '', description: '' });
  const [subCategoryForm, setSubCategoryForm] = useState({ subCategoryCode: '', subCategoryName: '', description: '', categoryId: '' });
  const [itemForm, setItemForm] = useState({
    itemCode: '',
    itemName: '',
    description: '',
    unitOfMeasure: 'Pcs',
    barcode: '',
    hsCode: '',
    purchasePrice: 0,
    salePrice: 0,
    minimumPrice: 0,
    wholesalePrice: 0,
    dealerPrice: 0,
    mrp: 0,
    taxable: false,
    gstRate: 18,
    thirdSchedule: false,   // module sales_third_schedule_tax: GST on gross (before discount) when true
    trackingType: 'None',
    reorderQuantity: 0,
    averageCost: 0,
    categoryId: '',
    subCategoryId: '',
    imageUrl: '',
    units: [],
    defaultPurchaseUnit: '',
    defaultSaleUnit: '',
    itemType: 'product',
    // Size / dimensional inventory (module `item_has_size`). When `hasSize` is
    // true, transaction line forms accept L × H × Thickness + Pieces and store
    // the line `quantity` as computed SQft so stockFlow stays in one unit.
    hasSize: false,
    soldByWeight: false,    // module: pos_weight_scale (price per Kg, qty from scale)
    sizeUnit: 'inches',     // 'inches' | 'cm'
    defaultLength: 0,
    defaultHeight: 0,
    defaultThickness: 0,
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef(null);

  useEffect(() => {
    if (activeCompany?.id) {
      fetchAllData();
      loadDocumentFormats(activeCompany.id).then(cfg => {
        setPrintConfig(cfg);
        const saved = cfg?.itemCatalogue || {};
        setCatalogueOptions(prev => ({
          ...prev,
          design: saved.design || 'grid',
          columns: { ...(saved.columns || {}) },
        }));
      });
    }
  }, [activeCompany]);

  const handleOpenCataloguePrint = () => {
    const saved = printConfig?.itemCatalogue || {};
    setCatalogueOptions({
      design: saved.design || 'grid',
      categoryFilter: 'all',
      subCategoryFilter: 'all',
      columns: { ...(saved.columns || {}) },
    });
    setShowCatalogueModal(true);
  };

  const handlePrintCatalogue = () => {
    let filtered = items;
    let filterLabel = 'All Items';
    if (catalogueOptions.categoryFilter !== 'all') {
      filtered = filtered.filter(i => i.categoryId === catalogueOptions.categoryFilter);
      const c = categories.find(x => x.id === catalogueOptions.categoryFilter);
      filterLabel = `Category: ${c?.categoryName || ''}`;
    }
    if (catalogueOptions.subCategoryFilter !== 'all') {
      filtered = filtered.filter(i => i.subCategoryId === catalogueOptions.subCategoryFilter);
      const s = subCategories.find(x => x.id === catalogueOptions.subCategoryFilter);
      filterLabel = `Sub-Category: ${s?.subCategoryName || ''}`;
    }
    if (filtered.length === 0) { alert('No items match the selected filter.'); return; }

    const enriched = filtered.map(it => ({ ...it, availableQty: getItemStockQty(it) }));

    printItemCatalogue(
      enriched,
      {
        design: catalogueOptions.design,
        columns: catalogueOptions.columns,
        categories,
        subCategories,
        filterLabel,
      },
      activeCompany,
      printConfig
    );
    setShowCatalogueModal(false);
  };

  const toggleCatalogueColumn = (key) => {
    setCatalogueOptions(prev => ({
      ...prev,
      columns: { ...prev.columns, [key]: !(prev.columns[key] ?? defaultColumnOn(key)) },
    }));
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const tasks = [fetchCategories(), fetchSubCategories(), fetchItems(), fetchUnitsBank()];
      await Promise.all(tasks);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  const fetchUnitsBank = async () => {
    try {
      const q = query(
        collection(db, `companies/${activeCompany.id}/units`),
        orderBy('unitCode')
      );
      const snapshot = await getDocs(q);
      setUnitsBank(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error fetching units bank:', error);
      setUnitsBank([]);
    }
  };

  const fetchCategories = async () => {
    const q = query(
      collection(db, `companies/${activeCompany.id}/inventoryCategories`),
      orderBy('categoryCode')
    );
    const snapshot = await getDocs(q);
    setCategories(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(c => c.isDeleted !== true));
  };

  const fetchSubCategories = async () => {
    const q = query(
      collection(db, `companies/${activeCompany.id}/inventorySubCategories`),
      orderBy('subCategoryCode')
    );
    const snapshot = await getDocs(q);
    setSubCategories(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(s => s.isDeleted !== true));
  };

  const fetchItems = async () => {
    const q = query(
      collection(db, `companies/${activeCompany.id}/inventoryItems`),
      orderBy('itemCode')
    );
    const snapshot = await getDocs(q);
    setItems(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(i => i.isDeleted !== true));
  };

  const generateCategoryCode = () => {
    const maxCode = categories.reduce((max, cat) => {
      const num = parseInt(cat.categoryCode.replace('CAT-', '')) || 0;
      return num > max ? num : max;
    }, 0);
    return `CAT-${String(maxCode + 1).padStart(3, '0')}`;
  };

  const generateSubCategoryCode = () => {
    const maxCode = subCategories.reduce((max, sub) => {
      const num = parseInt(sub.subCategoryCode.replace('SCAT-', '')) || 0;
      return num > max ? num : max;
    }, 0);
    return `SCAT-${String(maxCode + 1).padStart(3, '0')}`;
  };

  const generateItemCode = () => {
    const maxCode = items.reduce((max, item) => {
      const num = parseInt(item.itemCode.replace('ITM-', '')) || 0;
      return num > max ? num : max;
    }, 0);
    return `ITM-${String(maxCode + 1).padStart(5, '0')}`;
  };

  // Category handlers
  const handleAddCategory = () => {
    setCategoryForm({ categoryCode: generateCategoryCode(), categoryName: '', description: '' });
    setEditingCategory(null);
    setShowCategoryModal(true);
  };

  const handleEditCategory = (category) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    setCategoryForm({
      categoryCode: category.categoryCode,
      categoryName: category.categoryName,
      description: category.description || ''
    });
    setEditingCategory(category);
    setShowCategoryModal(true);
  };

  const handleSaveCategory = async () => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (!categoryForm.categoryName.trim()) {
      alert('Category name is required');
      return;
    }

    try {
      if (editingCategory) {
        await updateDoc(doc(db, `companies/${activeCompany.id}/inventoryCategories`, editingCategory.id), {
          ...categoryForm,
          updatedAt: serverTimestamp()
        });
      } else {
        await addDoc(collection(db, `companies/${activeCompany.id}/inventoryCategories`), {
          ...categoryForm,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      setShowCategoryModal(false);
      clearCategoryCaches(activeCompany.id);
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      alert('Failed to save category');
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    const linkedSubCategories = subCategories.filter(sub => sub.categoryId === category.id);
    if (linkedSubCategories.length > 0) {
      alert('Cannot delete category with sub-categories. Please delete sub-categories first.');
      return;
    }

    if (window.confirm(`Are you sure you want to delete "${category.categoryName}"?`)) {
      try {
        await updateDoc(doc(db, `companies/${activeCompany.id}/inventoryCategories`, category.id), {
          isDeleted: true,
          deletedAt: serverTimestamp(),
          deletedBy: userProfile?.uid || null,
          updatedAt: serverTimestamp()
        });
        clearCategoryCaches(activeCompany.id);
        fetchCategories();
      } catch (error) {
        console.error('Error deleting category:', error);
        alert('Failed to delete category');
      }
    }
  };

  // Sub-Category handlers
  const handleAddSubCategory = (categoryId) => {
    setSubCategoryForm({
      subCategoryCode: generateSubCategoryCode(),
      subCategoryName: '',
      description: '',
      categoryId: categoryId
    });
    setEditingSubCategory(null);
    setSelectedCategoryId(categoryId);
    setShowSubCategoryModal(true);
  };

  const handleEditSubCategory = (subCategory) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    setSubCategoryForm({
      subCategoryCode: subCategory.subCategoryCode,
      subCategoryName: subCategory.subCategoryName,
      description: subCategory.description || '',
      categoryId: subCategory.categoryId
    });
    setEditingSubCategory(subCategory);
    setShowSubCategoryModal(true);
  };

  const handleSaveSubCategory = async () => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (!subCategoryForm.subCategoryName.trim()) {
      alert('Sub-category name is required');
      return;
    }

    try {
      if (editingSubCategory) {
        await updateDoc(doc(db, `companies/${activeCompany.id}/inventorySubCategories`, editingSubCategory.id), {
          ...subCategoryForm,
          updatedAt: serverTimestamp()
        });
      } else {
        await addDoc(collection(db, `companies/${activeCompany.id}/inventorySubCategories`), {
          ...subCategoryForm,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      setShowSubCategoryModal(false);
      clearCategoryCaches(activeCompany.id);
      fetchSubCategories();
    } catch (error) {
      console.error('Error saving sub-category:', error);
      alert('Failed to save sub-category');
    }
  };

  const handleDeleteSubCategory = async (subCategory) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    const linkedItems = items.filter(item => item.subCategoryId === subCategory.id);
    if (linkedItems.length > 0) {
      alert('Cannot delete sub-category with items. Please delete items first.');
      return;
    }

    if (window.confirm(`Are you sure you want to delete "${subCategory.subCategoryName}"?`)) {
      try {
        await updateDoc(doc(db, `companies/${activeCompany.id}/inventorySubCategories`, subCategory.id), {
          isDeleted: true,
          deletedAt: serverTimestamp(),
          deletedBy: userProfile?.uid || null,
          updatedAt: serverTimestamp()
        });
        clearCategoryCaches(activeCompany.id);
        fetchSubCategories();
      } catch (error) {
        console.error('Error deleting sub-category:', error);
        alert('Failed to delete sub-category');
      }
    }
  };

  // Item handlers
  const handleAddItem = (categoryId, subCategoryId) => {
    setItemForm({
      itemCode: generateItemCode(),
      itemName: '',
      description: '',
      unitOfMeasure: 'Pcs',
      barcode: '',
      hsCode: '',
      purchasePrice: 0,
      salePrice: 0,
      minimumPrice: 0,
      wholesalePrice: 0,
      dealerPrice: 0,
      mrp: 0,
      taxable: false,
      gstRate: 18,
      thirdSchedule: false,
      trackingType: 'None',
      reorderQuantity: 0,
      averageCost: 0,
      categoryId: categoryId,
      subCategoryId: subCategoryId,
      imageUrl: '',
      units: [],
      defaultPurchaseUnit: '',
      defaultSaleUnit: '',
      itemType: 'product',
      hasSize: false,
      soldByWeight: false,
      sizeUnit: 'inches',
      defaultLength: 0,
      defaultHeight: 0,
      defaultThickness: 0,
    });
    setImageFile(null);
    setImagePreview('');
    setEditingItem(null);
    setItemModalTab('basic');
    setShowItemModal(true);
  };

  const handleEditItem = (item) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    setItemForm({
      itemCode: item.itemCode,
      itemName: item.itemName,
      description: item.description || '',
      unitOfMeasure: item.unitOfMeasure || 'Pcs',
      barcode: item.barcode || '',
      hsCode: item.hsCode || '',
      purchasePrice: item.purchasePrice || 0,
      salePrice: item.salePrice || 0,
      minimumPrice: item.minimumPrice || 0,
      wholesalePrice: item.wholesalePrice || 0,
      dealerPrice: item.dealerPrice || 0,
      mrp: item.mrp || 0,
      taxable: item.taxable === true,
      gstRate: item.gstRate ?? 18,
      thirdSchedule: item.thirdSchedule === true,
      trackingType: item.trackingType || 'None',
      reorderQuantity: item.reorderQuantity || 0,
      averageCost: item.averageCost || 0,
      categoryId: item.categoryId,
      subCategoryId: item.subCategoryId,
      imageUrl: item.imageUrl || '',
      units: Array.isArray(item.units) ? item.units : [],
      defaultPurchaseUnit: item.defaultPurchaseUnit || '',
      defaultSaleUnit: item.defaultSaleUnit || '',
      ...(Array.isArray(item.showUnits) ? { showUnits: item.showUnits } : {}),
      itemType: item.itemType === 'service' ? 'service' : 'product',
      hasSize: !!item.hasSize,
      soldByWeight: item.soldByWeight === true,
      sizeUnit: item.sizeUnit === 'cm' ? 'cm' : 'inches',
      defaultLength: Number(item.defaultLength) || 0,
      defaultHeight: Number(item.defaultHeight) || 0,
      defaultThickness: Number(item.defaultThickness) || 0,
    });
    setImageFile(null);
    setImagePreview(item.imageUrl || '');
    setEditingItem(item);
    setItemModalTab('basic');
    setShowItemModal(true);
  };

  const handleSaveItem = async () => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (!itemForm.itemName.trim()) {
      alert('Item name is required');
      return;
    }

    const codeDup = await isItemCodeDuplicate(activeCompany?.id, itemForm.itemCode, editingItem?.id);
    if (codeDup) {
      alert('This item code is already in use'); return;
    }

    // Duplicate guard: if the NAME or BARCODE already exists on another item,
    // stop and require explicit approval before creating a duplicate.
    const nameNorm = itemForm.itemName.trim().toLowerCase();
    const barcodeNorm = (itemForm.barcode || '').trim();
    const dupItem = items.find(it => it.id !== editingItem?.id && (
      (it.itemName || '').trim().toLowerCase() === nameNorm ||
      (barcodeNorm && (it.barcode || '').trim() === barcodeNorm)
    ));
    if (dupItem) {
      const why = (barcodeNorm && (dupItem.barcode || '').trim() === barcodeNorm) ? 'barcode' : 'name';
      if (!window.confirm(`An item with this ${why} already exists:\n${dupItem.itemCode} — ${dupItem.itemName}\n\nAdd anyway?`)) return;
    }

    // Validate multi-unit array (only when feature is on AND user has added units)
    let normalizedUnits = [];
    if (multiUnitEnabled && Array.isArray(itemForm.units) && itemForm.units.length > 0) {
      normalizedUnits = itemForm.units
        .filter(u => u && u.unitCode && u.unitCode.trim())
        .map(u => ({
          unitCode: u.unitCode.trim(),
          isBase: !!u.isBase,
          conversion: Number(u.conversion) || 1,
          salePrice: Number(u.salePrice) || 0,
          purchasePrice: Number(u.purchasePrice) || 0,
          barcode: (u.barcode || '').trim()
        }));
      const codes = new Set();
      let baseCount = 0;
      for (const u of normalizedUnits) {
        if (codes.has(u.unitCode)) { alert(`Duplicate unit: ${u.unitCode}`); return; }
        codes.add(u.unitCode);
        if (u.isBase) baseCount += 1;
        if (!Number.isFinite(u.conversion) || u.conversion <= 0) {
          alert(`Conversion for ${u.unitCode} must be greater than 0`); return;
        }
        if (u.isBase && u.conversion !== 1) {
          alert(`Base unit (${u.unitCode}) must have conversion = 1`); return;
        }
      }
      if (normalizedUnits.length > 0 && baseCount !== 1) {
        alert('Exactly one unit must be marked as the base unit'); return;
      }
    }

    // Show Two Units: both or none, two different units of this item. A unit
    // removed or renamed above drops out of the pair (its box shows "(none)").
    let showUnitsPair = [];
    if (twoUnitsEnabled) {
      const codesNow = new Set(normalizedUnits.map(u => u.unitCode));
      const [su1, su2] = [itemForm.showUnits?.[0] || '', itemForm.showUnits?.[1] || ''].map(c => (codesNow.has(c) ? c : ''));
      if (codesNow.size >= 2 && (su1 || su2)) {
        if (!su1 || !su2) { alert('Show Two Units: pick both units, or leave both empty.'); return; }
        if (su1 === su2) { alert('Show Two Units: pick two different units.'); return; }
        showUnitsPair = [su1, su2];
      }
    }

    try {
      setUploadingImage(true);
      let imageUrl = itemForm.imageUrl || '';

      // Upload image if a new file was selected
      if (imageFile) {
        try {
          const ext = imageFile.name.split('.').pop();
          const storagePath = `companies/${activeCompany.id}/items/${itemForm.itemCode || Date.now()}.${ext}`;
          const storageRef = ref(storage, storagePath);
          await uploadBytes(storageRef, imageFile);
          imageUrl = await getDownloadURL(storageRef);
        } catch (imgErr) {
          console.error('Image upload failed:', imgErr);
          alert('Image upload failed (storage permission issue). Item will be saved without image.');
        }
      }

      const isService = itemForm.itemType === 'service';
      const hasSize = !!itemForm.hasSize;
      const itemData = {
        ...itemForm,
        imageUrl,
        purchasePrice: parseFloat(itemForm.purchasePrice) || 0,
        salePrice: parseFloat(itemForm.salePrice) || 0,
        minimumPrice: parseFloat(itemForm.minimumPrice) || 0,
        wholesalePrice: parseFloat(itemForm.wholesalePrice) || 0,
        dealerPrice: parseFloat(itemForm.dealerPrice) || 0,
        mrp: parseFloat(itemForm.mrp) || 0,
        gstRate: parseFloat(itemForm.gstRate) || 0,
        reorderQuantity: isService ? 0 : (parseInt(itemForm.reorderQuantity) || 0),
        averageCost: parseFloat(itemForm.averageCost) || 0,
        units: multiUnitEnabled ? normalizedUnits : (itemForm.units || []),
        defaultPurchaseUnit: itemForm.defaultPurchaseUnit || '',
        defaultSaleUnit: itemForm.defaultSaleUnit || '',
        // Written only while the module is on; otherwise the item keeps whatever it had.
        ...(twoUnitsEnabled ? { showUnits: showUnitsPair } : {}),
        itemType: isService ? 'service' : 'product',
        trackingType: isService ? 'None' : (itemForm.trackingType || 'None'),
        // Size / dimensional inventory — persisted as numbers so line forms
        // can prefill defaults without parseFloat-ing again.
        hasSize,
        sizeUnit: hasSize ? (itemForm.sizeUnit === 'cm' ? 'cm' : 'inches') : (itemForm.sizeUnit || 'inches'),
        defaultLength: parseFloat(itemForm.defaultLength) || 0,
        defaultHeight: parseFloat(itemForm.defaultHeight) || 0,
        defaultThickness: parseFloat(itemForm.defaultThickness) || 0,
        // When Has Size is on, the displayed UoM is always SQft (already
        // forced via the checkbox handler, but assert it here as a safety net).
        // "Sold by weight" (POS weight-scale plugin) forces Kg; the two are
        // mutually exclusive (size wins if both somehow set).
        unitOfMeasure: hasSize ? 'SQft' : (itemForm.soldByWeight ? 'Kg' : itemForm.unitOfMeasure),
        soldByWeight: !hasSize && itemForm.soldByWeight === true,
      };

      // Write the saved row into the device's local item cache IMMEDIATELY.
      //
      // Why: invalidateMaster() below is fire-and-forget and triggers a FULL
      // re-download of every item (~2,000 docs). A user who saves an item and
      // goes straight to an invoice gets there first, so the form reads the
      // cache before that download lands and the new item is missing — until a
      // hard refresh or re-login remounts the form. Writing the one row here
      // makes it available instantly, and costs nothing (local storage only).
      //
      // `itemData` carries no serverTimestamp sentinels — those are added in the
      // spreads below — so what goes into the cache is plain, storable data.
      let savedId = editingItem?.id || null;
      if (editingItem) {
        // INV-04: the average cost is kept by stock postings — an item edit must
        // never write back the (possibly older) figure the form loaded.
        const { averageCost: _keepAverage, ...editData } = itemData;
        await updateDoc(doc(db, `companies/${activeCompany.id}/inventoryItems`, editingItem.id), {
          ...editData,
          updatedAt: serverTimestamp()
        });
      } else {
        const ref = await addDoc(collection(db, `companies/${activeCompany.id}/inventoryItems`), {
          ...itemData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
        savedId = ref.id;
      }
      if (savedId) {
        await upsertLocalMaster(activeCompany?.id, 'inventoryItems', { id: savedId, ...itemData });
      }

      // Note: Opening stock should be entered via Opening Balances page
      // The currentStock field here is for display purposes only

      invalidateMaster(activeCompany?.id, 'inventoryItems'); // reconcile in the background
      setShowItemModal(false);
      setImageFile(null);
      setImagePreview('');
      fetchItems();
    } catch (error) {
      console.error('Error saving item:', error);
      alert('Failed to save item');
    } finally {
      setUploadingImage(false);
    }
  };

  // Keyboard shortcuts: Ctrl+N to add category, Ctrl+S to save active modal
  useDocumentShortcuts({
    onNew: handleAddCategory,
    onSave: showCategoryModal ? handleSaveCategory : showSubCategoryModal ? handleSaveSubCategory : showItemModal ? handleSaveItem : undefined,
    viewMode: (showCategoryModal || showSubCategoryModal || showItemModal) ? 'form' : 'list',
    saving: false
  });

  const handleDeleteItem = async (item) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (window.confirm(`Are you sure you want to delete "${item.itemName}"?`)) {
      try {
        const { hasReferences, message } = await checkEntityReferences(activeCompany.id, 'inventoryItem', item.id);
        if (hasReferences) {
          alert(message);
          return;
        }

        await updateDoc(doc(db, `companies/${activeCompany.id}/inventoryItems`, item.id), {
          isDeleted: true,
          deletedAt: serverTimestamp(),
          deletedBy: userProfile?.uid || null,
          updatedAt: serverTimestamp()
        });

        // Best-effort cleanup of any customer-pricing rows for this item.
        // Always runs (regardless of module state) so toggling can't leave
        // ghost rows pointing at a deleted item.
        try {
          const pricingSnap = await getDocs(query(
            collection(db, 'companies', activeCompany.id, 'customerItemPricing'),
            where('itemId', '==', item.id)
          ));
          if (!pricingSnap.empty) {
            const batch = writeBatch(db);
            pricingSnap.docs.forEach(d => batch.delete(d.ref));
            await batch.commit();
          }
        } catch (cleanupErr) {
          console.warn('Failed to clean up customer pricing rows on item delete:', cleanupErr);
        }
        // Same for vendor price lists (only when that module is on — no read otherwise).
        if (isVendorPricingOn(isModuleEnabled)) {
          try {
            const vpSnap = await getDocs(query(
              collection(db, 'companies', activeCompany.id, VENDOR_PRICING_COLLECTION),
              where('itemId', '==', item.id)
            ));
            if (!vpSnap.empty) {
              const batch = writeBatch(db);
              vpSnap.docs.forEach(d => batch.delete(d.ref));
              await batch.commit();
            }
          } catch (cleanupErr) {
            console.warn('Failed to clean up vendor pricing rows on item delete:', cleanupErr);
          }
        }

        await writeAuditLog(activeCompany?.id, null, 'DELETE', 'inventoryItem', item.id, `Deleted item: ${item.itemCode} - ${item.itemName}`);
        invalidateMaster(activeCompany?.id, 'inventoryItems');
        fetchItems();
      } catch (error) {
        console.error('Error deleting item:', error);
        alert('Failed to delete item');
      }
    }
  };

  // Excel Template Download - Single Sheet
  const downloadExcelTemplate = () => {
    // Single sheet with all data - Categories and SubCategories will be auto-created from names
    const templateData = [
      {
        'Category Name': 'Electronics',
        'Sub-Category Name': 'Mobile Phones',
        'Item Code': 'ITM-00001',
        'Item Name': 'iPhone 15 Pro',
        'Description': 'Apple iPhone 15 Pro 256GB',
        'Unit of Measure': 'Pcs',
        'Barcode': '1234567890123',
        'Purchase Price': 180000,
        'Sale Price': 210000,
        'Minimum Price': 200000,
        'Wholesale Price': 205000,
        'Dealer Price': 195000,
        'Taxable (Yes/No)': 'Yes',
        [`${taxLabel} Rate (%)`]: 17,
        'Tracking Type': 'Serial',
        'Reorder Level': 5,
        'Reorder Quantity': 10
      },
      {
        'Category Name': 'Electronics',
        'Sub-Category Name': 'Mobile Phones',
        'Item Code': 'ITM-00002',
        'Item Name': 'Samsung Galaxy S24',
        'Description': 'Samsung Galaxy S24 Ultra 512GB',
        'Unit of Measure': 'Pcs',
        'Barcode': '1234567890124',
        'Purchase Price': 160000,
        'Sale Price': 195000,
        'Minimum Price': 185000,
        'Wholesale Price': 190000,
        'Dealer Price': 180000,
        'Taxable (Yes/No)': 'Yes',
        [`${taxLabel} Rate (%)`]: 17,
        'Tracking Type': 'Serial',
        'Reorder Level': 5,
        'Reorder Quantity': 10
      },
      {
        'Category Name': 'Electronics',
        'Sub-Category Name': 'Laptops',
        'Item Code': 'ITM-00003',
        'Item Name': 'Dell XPS 15',
        'Description': 'Dell XPS 15 Intel i7 16GB RAM',
        'Unit of Measure': 'Pcs',
        'Barcode': '1234567890125',
        'Purchase Price': 250000,
        'Sale Price': 290000,
        'Minimum Price': 275000,
        'Wholesale Price': 280000,
        'Dealer Price': 270000,
        'Taxable (Yes/No)': 'Yes',
        [`${taxLabel} Rate (%)`]: 17,
        'Tracking Type': 'Serial',
        'Reorder Level': 3,
        'Reorder Quantity': 5
      },
      {
        'Category Name': 'Furniture',
        'Sub-Category Name': 'Office Chairs',
        'Item Code': 'ITM-00004',
        'Item Name': 'Ergonomic Office Chair',
        'Description': 'High-back ergonomic chair with lumbar support',
        'Unit of Measure': 'Pcs',
        'Barcode': '1234567890126',
        'Purchase Price': 25000,
        'Sale Price': 35000,
        'Minimum Price': 30000,
        'Wholesale Price': 32000,
        'Dealer Price': 28000,
        'Taxable (Yes/No)': 'Yes',
        [`${taxLabel} Rate (%)`]: 17,
        'Tracking Type': 'None',
        'Reorder Level': 10,
        'Reorder Quantity': 20
      },
      {
        'Category Name': 'Furniture',
        'Sub-Category Name': 'Office Desks',
        'Item Code': 'ITM-00005',
        'Item Name': 'Executive Office Desk',
        'Description': 'Wooden executive desk 6x3 ft',
        'Unit of Measure': 'Pcs',
        'Barcode': '1234567890127',
        'Purchase Price': 45000,
        'Sale Price': 65000,
        'Minimum Price': 55000,
        'Wholesale Price': 60000,
        'Dealer Price': 52000,
        'Taxable (Yes/No)': 'Yes',
        [`${taxLabel} Rate (%)`]: 17,
        'Tracking Type': 'None',
        'Reorder Level': 5,
        'Reorder Quantity': 10
      }
    ];

    // Create workbook
    const wb = XLSX.utils.book_new();

    // 3rd Schedule column — only when the module is on, so other companies'
    // templates stay exactly as before. Wholesome fills "3rd Schedule" or "Normal".
    if (thirdScheduleEnabled) {
      templateData.forEach((r, idx) => { r['Sale Tax Type'] = idx === 0 ? '3rd Schedule' : 'Normal'; });
    }

    // Add Items Sheet
    const wsItems = XLSX.utils.json_to_sheet(templateData);
    wsItems['!cols'] = [
      { wch: 18 }, { wch: 20 }, { wch: 12 }, { wch: 28 }, { wch: 35 },
      { wch: 15 }, { wch: 15 }, { wch: 14 }, { wch: 12 }, { wch: 14 },
      { wch: 15 }, { wch: 12 }, { wch: 15 }, { wch: 12 }, { wch: 14 },
      { wch: 12 }, { wch: 15 }
    ];
    XLSX.utils.book_append_sheet(wb, wsItems, 'Inventory Items');

    // Add Instructions Sheet
    const instructionsData = [
      { 'Instructions': '=== INVENTORY IMPORT TEMPLATE ===' },
      { 'Instructions': '' },
      { 'Instructions': 'HOW IT WORKS:' },
      { 'Instructions': '• Enter all your items in the "Inventory Items" sheet' },
      { 'Instructions': '• Categories and Sub-Categories are AUTO-CREATED from names you enter' },
      { 'Instructions': '• Items with same Category Name will be grouped under one category' },
      { 'Instructions': '• Items with same Sub-Category Name (within a category) will be grouped together' },
      { 'Instructions': '' },
      { 'Instructions': 'REQUIRED COLUMNS:' },
      { 'Instructions': '• Category Name - Name of the category (e.g., Electronics, Furniture)' },
      { 'Instructions': '• Sub-Category Name - Name of sub-category (e.g., Mobile Phones, Laptops)' },
      { 'Instructions': '• Item Code - Unique code for item (e.g., ITM-00001)' },
      { 'Instructions': '• Item Name - Name of the item' },
      { 'Instructions': '' },
      { 'Instructions': 'OPTIONAL COLUMNS:' },
      { 'Instructions': '• Description - Item description' },
      { 'Instructions': '• Unit of Measure - Pcs, Kg, Gm, Ltr, Ml, Mtr, Ft, Box, Pack, Dozen, Set, Pair' },
      { 'Instructions': '• Barcode - Product barcode' },
      { 'Instructions': '• Purchase Price - Cost price' },
      { 'Instructions': '• Sale Price - Selling price' },
      { 'Instructions': '• Minimum Price - Minimum allowed price' },
      { 'Instructions': '• Wholesale Price - Price for wholesalers' },
      { 'Instructions': '• Dealer Price - Price for dealers' },
      { 'Instructions': '• Taxable (Yes/No) - Is item taxable? (Yes or No)' },
      { 'Instructions': `• ${taxLabel} Rate (%) - ${taxLabel} percentage (default: 17)` },
      { 'Instructions': '• Tracking Type - None, Batch, or Serial' },
      { 'Instructions': '• Reorder Level - Minimum stock level' },
      { 'Instructions': '• Reorder Quantity - Quantity to reorder' },
      { 'Instructions': '' },
      { 'Instructions': 'NOTE: Opening stock is NOT set here. Load opening quantity & cost from:' },
      { 'Instructions': '      Master > Opening Balances > Inventory > Download Template (all items pre-filled).' },
      { 'Instructions': '' },
      { 'Instructions': 'EXAMPLE:' },
      { 'Instructions': 'If you enter 3 items with Category "Electronics" and Sub-Category "Mobile Phones",' },
      { 'Instructions': 'the system will create:' },
      { 'Instructions': '  1. One category called "Electronics"' },
      { 'Instructions': '  2. One sub-category called "Mobile Phones" under Electronics' },
      { 'Instructions': '  3. All 3 items under that sub-category' },
      { 'Instructions': '' },
      { 'Instructions': 'NOTE: Delete sample data before importing your actual data!' }
    ];
    const wsInstr = XLSX.utils.json_to_sheet(instructionsData);
    wsInstr['!cols'] = [{ wch: 85 }];
    XLSX.utils.book_append_sheet(wb, wsInstr, 'Instructions');

    // Download file
    XLSX.writeFile(wb, 'Inventory_Import_Template.xlsx');
  };

  // Excel Import
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      processExcelFile(file);
    }
    // Reset input
    event.target.value = '';
  };

  const processExcelFile = async (file) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    setImporting(true);
    setImportResults(null);
    setShowImportModal(true);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);

      const results = {
        categories: { success: 0, failed: 0, skipped: 0, errors: [] },
        subCategories: { success: 0, failed: 0, skipped: 0, errors: [] },
        items: { success: 0, failed: 0, errors: [] }
      };

      // Find the data sheet (first sheet that's not Instructions)
      const dataSheetName = workbook.SheetNames.find(name => name !== 'Instructions') || workbook.SheetNames[0];
      const sheet = workbook.Sheets[dataSheetName];
      const rows = XLSX.utils.sheet_to_json(sheet);

      if (rows.length === 0) {
        setImportResults({ error: 'No data found in the Excel file' });
        setImporting(false);
        return;
      }

      // Step 1: Extract unique categories and sub-categories from names
      setImportProgress({ current: 0, total: 3, status: 'Analyzing data...' });

      // Map to store category name -> id
      const categoryNameToId = {};
      // Map to store "categoryName|subCategoryName" -> id
      const subCategoryKeyToId = {};

      // Load existing categories and sub-categories by name
      categories.forEach(cat => {
        categoryNameToId[cat.categoryName.toLowerCase().trim()] = cat.id;
      });
      subCategories.forEach(sub => {
        const cat = categories.find(c => c.id === sub.categoryId);
        if (cat) {
          const key = `${cat.categoryName.toLowerCase().trim()}|${sub.subCategoryName.toLowerCase().trim()}`;
          subCategoryKeyToId[key] = sub.id;
        }
      });

      // Extract unique categories from data
      const uniqueCategories = new Map();
      const uniqueSubCategories = new Map();

      rows.forEach(row => {
        const categoryName = row['Category Name']?.toString().trim();
        const subCategoryName = row['Sub-Category Name']?.toString().trim();

        if (categoryName) {
          const catKey = categoryName.toLowerCase();
          if (!uniqueCategories.has(catKey)) {
            uniqueCategories.set(catKey, categoryName);
          }
        }

        if (categoryName && subCategoryName) {
          const subCatKey = `${categoryName.toLowerCase()}|${subCategoryName.toLowerCase()}`;
          if (!uniqueSubCategories.has(subCatKey)) {
            uniqueSubCategories.set(subCatKey, { categoryName, subCategoryName });
          }
        }
      });

      // Step 2: Create categories
      setImportProgress({ current: 1, total: 3, status: 'Creating Categories...' });

      let catCounter = categories.length;
      for (const [catKey, categoryName] of uniqueCategories) {
        if (categoryNameToId[catKey]) {
          // Category already exists, skip
          results.categories.skipped++;
          continue;
        }

        try {
          catCounter++;
          const categoryCode = `CAT-${String(catCounter).padStart(3, '0')}`;

          const docRef = await addDoc(collection(db, `companies/${activeCompany.id}/inventoryCategories`), {
            categoryCode,
            categoryName,
            description: '',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });

          categoryNameToId[catKey] = docRef.id;
          results.categories.success++;
        } catch (error) {
          results.categories.errors.push(`Category "${categoryName}": ${error.message}`);
          results.categories.failed++;
        }
      }

      // Step 3: Create sub-categories
      setImportProgress({ current: 2, total: 3, status: 'Creating Sub-Categories...' });

      let subCatCounter = subCategories.length;
      for (const [subCatKey, { categoryName, subCategoryName }] of uniqueSubCategories) {
        if (subCategoryKeyToId[subCatKey]) {
          // Sub-category already exists, skip
          results.subCategories.skipped++;
          continue;
        }

        const catKey = categoryName.toLowerCase().trim();
        const categoryId = categoryNameToId[catKey];

        if (!categoryId) {
          results.subCategories.errors.push(`Sub-Category "${subCategoryName}": Category "${categoryName}" not found`);
          results.subCategories.failed++;
          continue;
        }

        try {
          subCatCounter++;
          const subCategoryCode = `SCAT-${String(subCatCounter).padStart(3, '0')}`;

          const docRef = await addDoc(collection(db, `companies/${activeCompany.id}/inventorySubCategories`), {
            subCategoryCode,
            subCategoryName,
            categoryId,
            description: '',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });

          subCategoryKeyToId[subCatKey] = docRef.id;
          results.subCategories.success++;
        } catch (error) {
          results.subCategories.errors.push(`Sub-Category "${subCategoryName}": ${error.message}`);
          results.subCategories.failed++;
        }
      }

      // Step 4: Import items
      setImportProgress({ current: 0, total: rows.length, status: 'Importing Items...' });

      // Build a set of existing item codes
      const existingItemCodes = new Set(items.map(item => item.itemCode));
      // Duplicate guard (name + barcode): flag rows whose name or barcode already
      // exists, so imports don't silently create duplicate items.
      const existingItemNames = new Set(items.map(item => (item.itemName || '').trim().toLowerCase()).filter(Boolean));
      const existingBarcodes = new Set(items.map(item => (item.barcode || '').trim()).filter(Boolean));

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        try {
          const categoryName = row['Category Name']?.toString().trim();
          const subCategoryName = row['Sub-Category Name']?.toString().trim();
          const itemCode = row['Item Code']?.toString().trim();
          const itemName = row['Item Name']?.toString().trim();

          if (!categoryName || !subCategoryName || !itemCode || !itemName) {
            results.items.errors.push(`Row ${i + 2}: Missing required fields (Category Name, Sub-Category Name, Item Code, or Item Name)`);
            results.items.failed++;
            continue;
          }

          // Check if item code already exists
          if (existingItemCodes.has(itemCode)) {
            results.items.errors.push(`Row ${i + 2}: Item Code "${itemCode}" already exists`);
            results.items.failed++;
            continue;
          }

          // Duplicate name / barcode guard — skip & flag instead of creating a duplicate
          const rowNameKey = itemName.toLowerCase();
          const rowBarcode = row['Barcode']?.toString().trim() || '';
          if (existingItemNames.has(rowNameKey)) {
            results.items.errors.push(`Row ${i + 2}: Item Name "${itemName}" already exists — skipped (duplicate)`);
            results.items.failed++;
            continue;
          }
          if (rowBarcode && existingBarcodes.has(rowBarcode)) {
            results.items.errors.push(`Row ${i + 2}: Barcode "${rowBarcode}" already exists — skipped (duplicate)`);
            results.items.failed++;
            continue;
          }

          const catKey = categoryName.toLowerCase().trim();
          const subCatKey = `${categoryName.toLowerCase().trim()}|${subCategoryName.toLowerCase().trim()}`;

          const categoryId = categoryNameToId[catKey];
          const subCategoryId = subCategoryKeyToId[subCatKey];

          if (!categoryId) {
            results.items.errors.push(`Row ${i + 2}: Category "${categoryName}" not found`);
            results.items.failed++;
            continue;
          }

          if (!subCategoryId) {
            results.items.errors.push(`Row ${i + 2}: Sub-Category "${subCategoryName}" not found`);
            results.items.failed++;
            continue;
          }

          const taxableValue = row['Taxable (Yes/No)']?.toString().toLowerCase();
          const taxable = taxableValue === 'yes' || taxableValue === 'true' || taxableValue === '1' || taxableValue === undefined;

          const trackingType = row['Tracking Type']?.toString().trim() || 'None';
          const validTrackingTypes = ['None', 'Batch', 'Serial'];
          const finalTrackingType = validTrackingTypes.includes(trackingType) ? trackingType : 'None';

          const unitOfMeasure = row['Unit of Measure']?.toString().trim() || 'Pcs';

          await addDoc(collection(db, `companies/${activeCompany.id}/inventoryItems`), {
            itemCode,
            itemName,
            categoryId,
            subCategoryId,
            description: row['Description']?.toString().trim() || '',
            unitOfMeasure,
            barcode: row['Barcode']?.toString().trim() || '',
            purchasePrice: parseFloat(row['Purchase Price']) || 0,
            salePrice: parseFloat(row['Sale Price']) || 0,
            minimumPrice: parseFloat(row['Minimum Price']) || 0,
            wholesalePrice: parseFloat(row['Wholesale Price']) || 0,
            dealerPrice: parseFloat(row['Dealer Price']) || 0,
            taxable,
            gstRate: parseFloat(row[`${taxLabel} Rate (%)`]) || 17,
            // 3rd Schedule flag (module sales_third_schedule_tax). Accepts
            // "3rd Schedule" / "3rd" / "Yes" / "true"; anything else = Normal.
            // Harmless for companies without the module (defaults to false).
            thirdSchedule: ['3rd schedule', '3rd', 'yes', 'true'].includes(
              (row['Sale Tax Type'] ?? '').toString().trim().toLowerCase()
            ),
            trackingType: finalTrackingType,
            reorderLevel: parseInt(row['Reorder Level']) || 0,
            reorderQuantity: parseInt(row['Reorder Quantity']) || 0,
            // Opening stock/cost is NOT set here — it's a phantom write (no stock
            // movement) that the stock engine overwrites. Real opening is posted
            // from Opening Balances > Inventory (Import), which creates a proper
            // stockFlow row against the selected branch + location. The template
            // keeps the Opening Stock / Opening Cost columns so the SAME file is
            // reused there.
            currentStock: 0,
            averageCost: 0,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });

          existingItemCodes.add(itemCode);
          existingItemNames.add(rowNameKey);
          if (rowBarcode) existingBarcodes.add(rowBarcode);
          results.items.success++;
        } catch (error) {
          results.items.errors.push(`Row ${i + 2}: ${error.message}`);
          results.items.failed++;
        }
        setImportProgress({ current: i + 1, total: rows.length, status: 'Importing Items...' });
      }

      setImportResults(results);
      setImportProgress({ current: 0, total: 0, status: 'Import Complete!' });

      // Refresh data
      clearCategoryCaches(activeCompany.id);
      await fetchAllData();

    } catch (error) {
      console.error('Error processing Excel file:', error);
      setImportResults({
        error: `Failed to process file: ${error.message}`
      });
    }

    setImporting(false);
  };

  // ── Bulk item-image import ────────────────────────────────────────────────
  // User picks a folder; each image whose file name = an Item Code (e.g.
  // ITM-001.jpg) is uploaded and set as that item's picture. Same storage path
  // and `imageUrl` field as the single-item uploader. Up to 5MB each.
  const handleImportImages = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-picking the same folder
    const files = picked.filter(f => /\.(jpe?g|png|webp|gif)$/i.test(f.name));
    if (files.length === 0) {
      alert('No image files found. Name each image with its Item Code, e.g. ITM-001.jpg');
      return;
    }
    if (!window.confirm(
      `Attach ${files.length} image(s) to items by matching the file name to the Item Code?\n\n` +
      `Each file must be named exactly like the item code (e.g. ITM-001.jpg). Max 5MB each.`
    )) return;

    // Item Code → item (case-insensitive).
    const byCode = new Map();
    items.forEach(it => { if (it.itemCode) byCode.set(String(it.itemCode).toLowerCase().trim(), it); });

    const MAX = 5 * 1024 * 1024;
    setImportingImages(true);
    setImageImportProgress({ current: 0, total: files.length });
    const res = { attached: 0, notMatched: [], tooLarge: [], failed: [] };

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const base = file.name.replace(/\.[^.]+$/, '').trim(); // strip extension → item code
      const item = byCode.get(base.toLowerCase());
      try {
        if (!item) { res.notMatched.push(file.name); }
        else if (file.size > MAX) { res.tooLarge.push(file.name); }
        else {
          const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
          const storageRef = ref(storage, `companies/${activeCompany.id}/items/${item.itemCode}.${ext}`);
          await uploadBytes(storageRef, file);
          const imageUrl = await getDownloadURL(storageRef);
          await updateDoc(doc(db, `companies/${activeCompany.id}/inventoryItems`, item.id), { imageUrl, updatedAt: serverTimestamp() });
          res.attached++;
        }
      } catch (err) {
        res.failed.push(`${file.name}: ${err.message}`);
      }
      setImageImportProgress({ current: i + 1, total: files.length });
    }

    setImportingImages(false);
    try { await fetchAllData(); } catch (_) { /* refresh best-effort */ }

    // Show the full result (every mismatch/error listed) in a panel, not a toast.
    setImageImportResult(res);
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const toggleSubCategory = (subCategoryId) => {
    setExpandedSubCategories(prev => ({
      ...prev,
      [subCategoryId]: !prev[subCategoryId]
    }));
  };

  const getSubCategoriesForCategory = (categoryId) => {
    return subCategories.filter(sub => sub.categoryId === categoryId);
  };

  const getItemsForSubCategory = (subCategoryId) => {
    return items.filter(item => item.subCategoryId === subCategoryId);
  };

  // Enhanced search filtering - searches items, categories, and subcategories
  const searchLower = searchTerm.toLowerCase().trim();

  const matchingItems = searchLower ? items.filter(item =>
    item.itemName?.toLowerCase().includes(searchLower) ||
    item.itemCode?.toLowerCase().includes(searchLower) ||
    item.barcode?.toLowerCase().includes(searchLower)
  ) : [];

  const matchingSubCategories = searchLower ? subCategories.filter(sub =>
    sub.subCategoryName?.toLowerCase().includes(searchLower) ||
    sub.subCategoryCode?.toLowerCase().includes(searchLower)
  ) : [];

  const matchingCategories = searchLower ? categories.filter(cat =>
    cat.categoryName?.toLowerCase().includes(searchLower) ||
    cat.categoryCode?.toLowerCase().includes(searchLower)
  ) : [];

  // Get IDs of subcategories and categories that contain matching items
  const subCategoryIdsWithMatches = new Set([
    ...matchingItems.map(item => item.subCategoryId),
    ...matchingSubCategories.map(sub => sub.id)
  ]);

  const categoryIdsWithMatches = new Set([
    ...matchingCategories.map(cat => cat.id),
    ...subCategories
      .filter(sub => subCategoryIdsWithMatches.has(sub.id))
      .map(sub => sub.categoryId)
  ]);

  // Filter categories to show only those with matches (or all if no search)
  const filteredCategories = searchLower
    ? categories.filter(cat => categoryIdsWithMatches.has(cat.id))
    : categories;

  // Auto-expand categories and subcategories when searching
  React.useEffect(() => {
    if (searchLower) {
      const newExpandedCategories = {};
      const newExpandedSubCategories = {};

      categoryIdsWithMatches.forEach(catId => {
        newExpandedCategories[catId] = true;
      });

      subCategoryIdsWithMatches.forEach(subId => {
        newExpandedSubCategories[subId] = true;
      });

      setExpandedCategories(prev => ({ ...prev, ...newExpandedCategories }));
      setExpandedSubCategories(prev => ({ ...prev, ...newExpandedSubCategories }));
    }
  }, [searchTerm]);

  // Check if an item matches the search
  const isItemMatch = (item) => {
    if (!searchLower) return false;
    return item.itemName?.toLowerCase().includes(searchLower) ||
           item.itemCode?.toLowerCase().includes(searchLower) ||
           item.barcode?.toLowerCase().includes(searchLower);
  };

  // Check if a subcategory matches the search
  const isSubCategoryMatch = (subCategory) => {
    if (!searchLower) return false;
    return subCategory.subCategoryName?.toLowerCase().includes(searchLower) ||
           subCategory.subCategoryCode?.toLowerCase().includes(searchLower);
  };

  // Check if a category matches the search
  const isCategoryMatch = (category) => {
    if (!searchLower) return false;
    return category.categoryName?.toLowerCase().includes(searchLower) ||
           category.categoryCode?.toLowerCase().includes(searchLower);
  };

  // Filter subcategories for a category
  const getFilteredSubCategories = (categoryId) => {
    const allSubs = subCategories.filter(sub => sub.categoryId === categoryId);
    if (!searchLower) return allSubs;
    return allSubs.filter(sub => subCategoryIdsWithMatches.has(sub.id));
  };

  // Filter items for a subcategory
  const getFilteredItems = (subCategoryId) => {
    const allItems = items.filter(item => item.subCategoryId === subCategoryId);
    if (!searchLower) return allItems;
    return allItems.filter(item => isItemMatch(item));
  };

  // Get total matches count
  const totalMatches = searchLower
    ? matchingItems.length + matchingSubCategories.length + matchingCategories.length
    : 0;

  const unitOptions = ['Pcs', 'Kg', 'Gm', 'Ltr', 'Ml', 'Mtr', 'Ft', 'Box', 'Pack', 'Dozen', 'Set', 'Pair'];

  // Resolve an item's on-hand quantity from the denormalized snapshot on the
  // item doc (refreshed on stock-IN postings). Costs zero extra reads; the
  // to-the-minute figure lives in Inventory Balances / Stock Summary reports.
  const getItemStockQty = (item) => {
    if (!item) return 0;
    // Rounded to 6 decimals: the saved figure is a running total and can
    // carry tiny leftovers (23.980000000000004 → 23.98); real decimals stay.
    return Math.round((Number(item?.currentStock) || 0) * 1e6) / 1e6;
  };

  // Render an item's current stock as dual-unit ("10 Ctn (240 Pcs)") when the
  // item has multiple units and a default sale unit. Otherwise plain qty.
  const formatItemStock = (item) => {
    const baseQty = getItemStockQty(item);
    if (!multiUnitEnabled) return baseQty;
    if (!Array.isArray(item?.units) || item.units.length < 2) return baseQty;
    const baseUnit = item.unitOfMeasure || (item.units.find(u => u.isBase)?.unitCode) || '';
    // Show Two Units: the item's chosen pair, e.g. "10 Ctn (240 Mtr)".
    const pair = twoUnitsEnabled ? getTwoUnitPair(item) : null;
    if (pair) {
      const fmt2 = (n) => Number.isInteger(n) ? n.toString() : n.toFixed(2);
      return `${fmt2(fromBaseQty(item, baseQty, pair[0]))} ${pair[0]} (${fmt2(fromBaseQty(item, baseQty, pair[1]))} ${pair[1]})`;
    }
    const targetCode = item.defaultSaleUnit
      || item.units.find(u => !u.isBase && Number(u.conversion) > 1)?.unitCode;
    if (!targetCode || targetCode === baseUnit) return baseQty;
    const targetRow = item.units.find(u => u.unitCode === targetCode);
    const conv = Number(targetRow?.conversion) || 1;
    if (conv <= 1) return baseQty;
    const targetQty = baseQty / conv;
    const fmt = (n) => Number.isInteger(n) ? n.toString() : n.toFixed(2);
    return `${fmt(targetQty)} ${targetCode} (${fmt(baseQty)} ${baseUnit})`;
  };

  return (
    <PageLayout pageTitle="Chart of Inventory" breadcrumb="Master Data / Chart of Inventory">
      <Container>
        <HeaderSection>
          <SearchBox>
            <FiSearch />
            <input
              type="text"
              placeholder="Search by item code, name, barcode, category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <ClearButton onClick={() => setSearchTerm('')}>
                <FiX />
              </ClearButton>
            )}
          </SearchBox>
          {searchLower && (
            <SearchResultsInfo>
              {totalMatches} result{totalMatches !== 1 ? 's' : ''} ({matchingItems.length} items)
            </SearchResultsInfo>
          )}
          <ButtonGroup>
            <ViewToggle>
              <ViewToggleBtn $active={viewMode === 'tree'} onClick={() => switchViewMode('tree')} title="Hierarchical view">
                <FiLayers /> Tree
              </ViewToggleBtn>
              <ViewToggleBtn $active={viewMode === 'simple'} onClick={() => switchViewMode('simple')} title="Flat 3-panel view">
                <FiGrid /> Simple
              </ViewToggleBtn>
            </ViewToggle>
            <TemplateButton onClick={downloadExcelTemplate}>
              <FiDownload /> Download Template
            </TemplateButton>
            {canEdit && (
              <ImportButton onClick={() => fileInputRef.current?.click()}>
                <FiUpload /> Import Excel
              </ImportButton>
            )}
            {canEdit && (
              <ImportButton onClick={() => imagesInputRef.current?.click()} disabled={importingImages} style={{ background: '#0ea5e9' }}
                title="Upload a folder of images named by Item Code (e.g. ITM-001.jpg) to attach them to items">
                <FiUpload /> {importingImages ? `Uploading ${imageImportProgress.current}/${imageImportProgress.total}…` : 'Import Images'}
              </ImportButton>
            )}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".xlsx,.xls"
              style={{ display: 'none' }}
            />
            <input
              type="file"
              ref={imagesInputRef}
              onChange={handleImportImages}
              accept="image/*"
              multiple
              webkitdirectory=""
              directory=""
              style={{ display: 'none' }}
            />
            <CatalogueButton onClick={handleOpenCataloguePrint} disabled={items.length === 0}>
              <FiPrinter /> Print Catalogue
            </CatalogueButton>
            {canEdit && (
              <AddButton onClick={handleAddCategory}>
                <FiPlus /> Add Category
              </AddButton>
            )}
          </ButtonGroup>
        </HeaderSection>

        <StatsRow>
          <StatCard>
            <StatIcon $color={colors.primary.main}><FiFolder /></StatIcon>
            <StatInfo>
              <StatValue>{categories.length}</StatValue>
              <StatLabel>Categories</StatLabel>
            </StatInfo>
          </StatCard>
          <StatCard>
            <StatIcon $color={colors.success.main}><FiPackage /></StatIcon>
            <StatInfo>
              <StatValue>{subCategories.length}</StatValue>
              <StatLabel>Sub-Categories</StatLabel>
            </StatInfo>
          </StatCard>
          <StatCard>
            <StatIcon $color={colors.info.main}><FiBox /></StatIcon>
            <StatInfo>
              <StatValue>{items.length}</StatValue>
              <StatLabel>Items</StatLabel>
            </StatInfo>
          </StatCard>
        </StatsRow>

        {loading ? (
          <LoadingMessage>Loading inventory data...</LoadingMessage>
        ) : viewMode === 'simple' ? (
          /* ─── SIMPLE 3-PANEL VIEW ─── */
          <SimpleLayout>
            <SimplePanel>
              <SimplePanelHeader>
                <span>Categories</span>
                <span style={{ color: colors.text.tertiary, fontSize: 11 }}>{categories.length}</span>
              </SimplePanelHeader>
              <SimpleList>
                <SimpleRow $active={selectedSimpleCategoryId === 'all'}
                  onClick={() => { setSelectedSimpleCategoryId('all'); setSelectedSimpleSubCategoryId('all'); }}>
                  <SimpleRowLeft><FiFolder /> <strong>All Categories</strong></SimpleRowLeft>
                  <SimpleCount>{items.length}</SimpleCount>
                </SimpleRow>
                {(searchLower
                  ? categories.filter(c => c.categoryName?.toLowerCase().includes(searchLower) || c.categoryCode?.toLowerCase().includes(searchLower))
                  : categories
                ).map(cat => {
                  const itemCount = items.filter(it => it.categoryId === cat.id).length;
                  return (
                    <SimpleRow key={cat.id}
                      $active={selectedSimpleCategoryId === cat.id}
                      onClick={() => { setSelectedSimpleCategoryId(cat.id); setSelectedSimpleSubCategoryId('all'); }}>
                      <SimpleRowLeft>
                        <FiFolder />
                        <SimpleCode>{cat.categoryCode}</SimpleCode>
                        <span>{cat.categoryName}</span>
                      </SimpleRowLeft>
                      <SimpleRowRight>
                        <SimpleCount>{itemCount}</SimpleCount>
                        {canEdit && (
                          <SimpleActions>
                            <ActionButton onClick={(e) => { e.stopPropagation(); handleEditCategory(cat); }} title="Edit"><FiEdit2 /></ActionButton>
                            {canDelete && <ActionButton $danger onClick={(e) => { e.stopPropagation(); handleDeleteCategory(cat); }} title="Delete"><FiTrash2 /></ActionButton>}
                          </SimpleActions>
                        )}
                      </SimpleRowRight>
                    </SimpleRow>
                  );
                })}
              </SimpleList>
              {canEdit && (
                <SimpleAddBtn onClick={handleAddCategory}>
                  <FiPlus /> Add Category
                </SimpleAddBtn>
              )}
            </SimplePanel>

            <SimplePanel>
              <SimplePanelHeader>
                <span>Sub-Categories</span>
                <span style={{ color: colors.text.tertiary, fontSize: 11 }}>
                  {(selectedSimpleCategoryId === 'all' ? subCategories : subCategories.filter(s => s.categoryId === selectedSimpleCategoryId)).length}
                </span>
              </SimplePanelHeader>
              <SimpleList>
                <SimpleRow $active={selectedSimpleSubCategoryId === 'all'}
                  onClick={() => setSelectedSimpleSubCategoryId('all')}>
                  <SimpleRowLeft><FiPackage /> <strong>All Sub-Categories</strong></SimpleRowLeft>
                  <SimpleCount>
                    {(selectedSimpleCategoryId === 'all' ? items : items.filter(i => i.categoryId === selectedSimpleCategoryId)).length}
                  </SimpleCount>
                </SimpleRow>
                {(() => {
                  const baseSubs = selectedSimpleCategoryId === 'all'
                    ? subCategories
                    : subCategories.filter(s => s.categoryId === selectedSimpleCategoryId);
                  const filtered = searchLower
                    ? baseSubs.filter(s => s.subCategoryName?.toLowerCase().includes(searchLower) || s.subCategoryCode?.toLowerCase().includes(searchLower))
                    : baseSubs;
                  if (filtered.length === 0) {
                    return <EmptySubMessage>No sub-categories.</EmptySubMessage>;
                  }
                  return filtered.map(sub => {
                    const itemCount = items.filter(i => i.subCategoryId === sub.id).length;
                    return (
                      <SimpleRow key={sub.id}
                        $active={selectedSimpleSubCategoryId === sub.id}
                        onClick={() => setSelectedSimpleSubCategoryId(sub.id)}>
                        <SimpleRowLeft>
                          <FiPackage />
                          <SimpleCode>{sub.subCategoryCode}</SimpleCode>
                          <span>{sub.subCategoryName}</span>
                        </SimpleRowLeft>
                        <SimpleRowRight>
                          <SimpleCount>{itemCount}</SimpleCount>
                          {canEdit && (
                            <SimpleActions>
                              <ActionButton onClick={(e) => { e.stopPropagation(); handleEditSubCategory(sub); }} title="Edit"><FiEdit2 /></ActionButton>
                              {canDelete && <ActionButton $danger onClick={(e) => { e.stopPropagation(); handleDeleteSubCategory(sub); }} title="Delete"><FiTrash2 /></ActionButton>}
                            </SimpleActions>
                          )}
                        </SimpleRowRight>
                      </SimpleRow>
                    );
                  });
                })()}
              </SimpleList>
              {canEdit && selectedSimpleCategoryId !== 'all' && (
                <SimpleAddBtn onClick={() => handleAddSubCategory(selectedSimpleCategoryId)}>
                  <FiPlus /> Add Sub-Category
                </SimpleAddBtn>
              )}
              {canEdit && selectedSimpleCategoryId === 'all' && (
                <SimpleAddBtnMuted title="Select a category to add a sub-category">
                  Select a category first
                </SimpleAddBtnMuted>
              )}
            </SimplePanel>

            <SimplePanel style={{ flex: 2 }}>
              <SimplePanelHeader>
                <span>
                  Items
                  {selectedSimpleCategoryId !== 'all' && (
                    <SimpleCrumb>
                      &nbsp;/ {categories.find(c => c.id === selectedSimpleCategoryId)?.categoryName || ''}
                      {selectedSimpleSubCategoryId !== 'all' && ` / ${subCategories.find(s => s.id === selectedSimpleSubCategoryId)?.subCategoryName || ''}`}
                    </SimpleCrumb>
                  )}
                </span>
                <span style={{ color: colors.text.tertiary, fontSize: 11 }}>
                  {(() => {
                    let list = items;
                    if (selectedSimpleCategoryId !== 'all') list = list.filter(i => i.categoryId === selectedSimpleCategoryId);
                    if (selectedSimpleSubCategoryId !== 'all') list = list.filter(i => i.subCategoryId === selectedSimpleSubCategoryId);
                    return list.length;
                  })()}
                </span>
              </SimplePanelHeader>

              <SimpleTableWrap>
                <SimpleTable>
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Item</th>
                      <th>UOM</th>
                      <th style={{ textAlign: 'right' }}>Sale Price</th>
                      <th style={{ textAlign: 'right' }}>Purchase</th>
                      <th>Tax</th>
                      <th style={{ textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      let list = items;
                      if (selectedSimpleCategoryId !== 'all') list = list.filter(i => i.categoryId === selectedSimpleCategoryId);
                      if (selectedSimpleSubCategoryId !== 'all') list = list.filter(i => i.subCategoryId === selectedSimpleSubCategoryId);
                      if (searchLower) {
                        list = list.filter(i =>
                          i.itemName?.toLowerCase().includes(searchLower) ||
                          i.itemCode?.toLowerCase().includes(searchLower) ||
                          i.barcode?.toLowerCase().includes(searchLower)
                        );
                      }
                      if (list.length === 0) {
                        return (
                          <tr><td colSpan={7} style={{ textAlign: 'center', color: colors.text.tertiary, padding: 24 }}>
                            No items match the current selection.
                          </td></tr>
                        );
                      }
                      return list.map(item => (
                        <tr key={item.id}>
                          <td><strong style={{ fontFamily: 'monospace', fontSize: 12 }}>{item.itemCode}</strong></td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              {item.imageUrl ? (
                                <img src={item.imageUrl} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4 }} />
                              ) : (
                                <div style={{ width: 32, height: 32, background: colors.neutral[100], borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <FiBox size={14} style={{ color: colors.text.tertiary }} />
                                </div>
                              )}
                              <div>
                                <div style={{ fontWeight: 500 }}>{item.itemName}</div>
                                {item.description && <div style={{ fontSize: 11, color: colors.text.tertiary }}>{item.description}</div>}
                              </div>
                            </div>
                          </td>
                          <td>{item.unitOfMeasure || '-'}</td>
                          <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{(Number(item.salePrice) || 0).toLocaleString('en-PK')}</td>
                          <td style={{ textAlign: 'right', fontFamily: 'monospace', color: colors.text.secondary }}>{(Number(item.purchasePrice) || 0).toLocaleString('en-PK')}</td>
                          <td style={{ fontSize: 11 }}>{item.taxable ? `${item.gstRate || 0}%` : '-'}</td>
                          <td style={{ textAlign: 'center' }}>
                            <ActionButton
                              data-action-trigger
                              onClick={(e) => toggleActionMenu(item.id, e)}
                              title="Actions"
                            >
                              <FiMoreVertical />
                            </ActionButton>
                          </td>
                        </tr>
                      ));
                    })()}
                  </tbody>
                </SimpleTable>
              </SimpleTableWrap>

              {canEdit && (selectedSimpleCategoryId !== 'all' && selectedSimpleSubCategoryId !== 'all') && (
                <SimpleAddBtn onClick={() => handleAddItem(selectedSimpleCategoryId, selectedSimpleSubCategoryId)}>
                  <FiPlus /> Add Item
                </SimpleAddBtn>
              )}
              {canEdit && (selectedSimpleCategoryId === 'all' || selectedSimpleSubCategoryId === 'all') && (
                <SimpleAddBtnMuted title="Pick a category and sub-category to add an item here">
                  Select a category and sub-category to add items
                </SimpleAddBtnMuted>
              )}
            </SimplePanel>
          </SimpleLayout>
        ) : (
          <TreeContainer>
            {filteredCategories.length === 0 ? (
              <EmptyMessage>
                {searchTerm ? 'No categories found matching your search.' : 'No categories found. Click "Add Category" to create one.'}
              </EmptyMessage>
            ) : (
              filteredCategories.map(category => (
                <CategoryNode key={category.id}>
                  <CategoryHeader $isMatch={isCategoryMatch(category)}>
                    <CategoryLeft onClick={() => toggleCategory(category.id)}>
                      {expandedCategories[category.id] ? <FiChevronDown /> : <FiChevronRight />}
                      <FiFolder />
                      <CategoryCode $isMatch={isCategoryMatch(category)}>{category.categoryCode}</CategoryCode>
                      <CategoryName $isMatch={isCategoryMatch(category)}>{category.categoryName}</CategoryName>
                      <CategoryCount>({getSubCategoriesForCategory(category.id).length} sub-categories)</CategoryCount>
                    </CategoryLeft>
                    <CategoryActions>
                      {canEdit && (
                        <ActionButton onClick={() => handleAddSubCategory(category.id)} title="Add Sub-Category">
                          <FiPlus />
                        </ActionButton>
                      )}
                      {canChange && (
                        <ActionButton onClick={() => handleEditCategory(category)} title="Edit">
                          <FiEdit2 />
                        </ActionButton>
                      )}
                      {canDelete && (
                        <ActionButton $danger onClick={() => handleDeleteCategory(category)} title="Delete">
                          <FiTrash2 />
                        </ActionButton>
                      )}
                    </CategoryActions>
                  </CategoryHeader>

                  {expandedCategories[category.id] && (
                    <SubCategoryList>
                      {getFilteredSubCategories(category.id).map(subCategory => (
                        <SubCategoryNode key={subCategory.id} $isMatch={isSubCategoryMatch(subCategory)}>
                          <SubCategoryHeader $isMatch={isSubCategoryMatch(subCategory)}>
                            <SubCategoryLeft onClick={() => toggleSubCategory(subCategory.id)}>
                              {expandedSubCategories[subCategory.id] ? <FiChevronDown /> : <FiChevronRight />}
                              <FiPackage />
                              <SubCategoryCode $isMatch={isSubCategoryMatch(subCategory)}>{subCategory.subCategoryCode}</SubCategoryCode>
                              <SubCategoryName $isMatch={isSubCategoryMatch(subCategory)}>{subCategory.subCategoryName}</SubCategoryName>
                              <SubCategoryCount>({getItemsForSubCategory(subCategory.id).length} items)</SubCategoryCount>
                            </SubCategoryLeft>
                            <CategoryActions>
                              {canEdit && (
                                <ActionButton onClick={() => handleAddItem(category.id, subCategory.id)} title="Add Item">
                                  <FiPlus />
                                </ActionButton>
                              )}
                              {canChange && (
                                <ActionButton onClick={() => handleEditSubCategory(subCategory)} title="Edit">
                                  <FiEdit2 />
                                </ActionButton>
                              )}
                              {canDelete && (
                                <ActionButton $danger onClick={() => handleDeleteSubCategory(subCategory)} title="Delete">
                                  <FiTrash2 />
                                </ActionButton>
                              )}
                            </CategoryActions>
                          </SubCategoryHeader>

                          {expandedSubCategories[subCategory.id] && (
                            <ItemList>
                              {getFilteredItems(subCategory.id).map(item => (
                                <ItemRow key={item.id} $isMatch={isItemMatch(item)}>
                                  <ItemLeft>
                                    <FiBox />
                                    <ItemCode $isMatch={isItemMatch(item)}>{item.itemCode}</ItemCode>
                                    <ItemName $isMatch={isItemMatch(item)}>{item.itemName}</ItemName>
                                    {item.barcode && isItemMatch(item) && item.barcode.toLowerCase().includes(searchLower) && (
                                      <ItemBarcode $isMatch={true}>{item.barcode}</ItemBarcode>
                                    )}
                                    <ItemUnit>{item.unitOfMeasure}</ItemUnit>
                                    <ItemPrice>{activeCompany?.currencySymbol || 'Rs.'} {item.salePrice?.toLocaleString()}</ItemPrice>
                                    <ItemStock $low={getItemStockQty(item) <= (item.reorderQuantity || 0)}>
                                      Stock: {formatItemStock(item)}
                                    </ItemStock>
                                  </ItemLeft>
                                  <CategoryActions>
                                    {canChange && (
                                      <ActionButton onClick={() => handleEditItem(item)} title="Edit">
                                        <FiEdit2 />
                                      </ActionButton>
                                    )}
                                    {canDelete && (
                                      <ActionButton $danger onClick={() => handleDeleteItem(item)} title="Delete">
                                        <FiTrash2 />
                                      </ActionButton>
                                    )}
                                  </CategoryActions>
                                </ItemRow>
                              ))}
                              {getFilteredItems(subCategory.id).length === 0 && (
                                <EmptyItemMessage>
                                  {searchLower ? 'No matching items' : 'No items in this sub-category'}
                                </EmptyItemMessage>
                              )}
                            </ItemList>
                          )}
                        </SubCategoryNode>
                      ))}
                      {getFilteredSubCategories(category.id).length === 0 && (
                        <EmptySubMessage>
                          {searchLower ? 'No matching sub-categories' : 'No sub-categories. Click + to add one.'}
                        </EmptySubMessage>
                      )}
                    </SubCategoryList>
                  )}
                </CategoryNode>
              ))
            )}
          </TreeContainer>
        )}
      </Container>

      {/* Item Actions Menu (portal — escapes table overflow) */}
      {openActionMenuId && (() => {
        const item = items.find(i => i.id === openActionMenuId);
        if (!item) return null;
        return createPortal(
          <ActionDropdown
            data-action-menu
            style={{ top: actionMenuPos.top, left: actionMenuPos.left }}
          >
            {canChange && (
              <ActionDropItem onClick={() => { closeActionMenu(); handleEditItem(item); }}>
                <FiEdit2 size={14} /> Edit
              </ActionDropItem>
            )}
            <ActionDropItem onClick={() => handleOpenInventoryLedger(item)}>
              <FiBookOpen size={14} /> Inventory Ledger
            </ActionDropItem>
            <ActionDropItem onClick={() => handleOpenItemPL(item)}>
              <FiTrendingUp size={14} /> Item P&amp;L
            </ActionDropItem>
            {canDelete && (
              <>
                <ActionDropDivider />
                <ActionDropItem $danger onClick={() => { closeActionMenu(); handleDeleteItem(item); }}>
                  <FiTrash2 size={14} /> Delete
                </ActionDropItem>
              </>
            )}
          </ActionDropdown>,
          document.body
        );
      })()}

      {/* Category Modal */}
      {showCategoryModal && (
        <ModalOverlay>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>{editingCategory ? 'Edit Category' : 'Add Category'}</h3>
              <CloseButton onClick={() => setShowCategoryModal(false)}>
                <FiX />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <label>Category Code</label>
                <input
                  type="text"
                  value={categoryForm.categoryCode}
                  onChange={(e) => setCategoryForm({ ...categoryForm, categoryCode: e.target.value })}
                  disabled={editingCategory}
                />
              </FormGroup>
              <FormGroup>
                <label>Category Name *</label>
                <input
                  type="text"
                  value={categoryForm.categoryName}
                  onChange={(e) => setCategoryForm({ ...categoryForm, categoryName: e.target.value })}
                  placeholder="Enter category name"
                />
              </FormGroup>
              <FormGroup>
                <label>Description</label>
                <textarea
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                  placeholder="Enter description"
                  rows={3}
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={() => setShowCategoryModal(false)}>Cancel</CancelButton>
              {canEdit && (
                <SaveButton onClick={handleSaveCategory}>
                  {editingCategory ? 'Update' : 'Save'}
                </SaveButton>
              )}
            </ModalFooter>
          </Modal>
        </ModalOverlay>
      )}

      {/* Sub-Category Modal */}
      {showSubCategoryModal && (
        <ModalOverlay>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>{editingSubCategory ? 'Edit Sub-Category' : 'Add Sub-Category'}</h3>
              <CloseButton onClick={() => setShowSubCategoryModal(false)}>
                <FiX />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormGroup>
                <label>Sub-Category Code</label>
                <input
                  type="text"
                  value={subCategoryForm.subCategoryCode}
                  onChange={(e) => setSubCategoryForm({ ...subCategoryForm, subCategoryCode: e.target.value })}
                  disabled={editingSubCategory}
                />
              </FormGroup>
              <FormGroup>
                <label>Sub-Category Name *</label>
                <input
                  type="text"
                  value={subCategoryForm.subCategoryName}
                  onChange={(e) => setSubCategoryForm({ ...subCategoryForm, subCategoryName: e.target.value })}
                  placeholder="Enter sub-category name"
                />
              </FormGroup>
              <FormGroup>
                <label>Parent Category</label>
                <select
                  value={subCategoryForm.categoryId}
                  onChange={(e) => setSubCategoryForm({ ...subCategoryForm, categoryId: e.target.value })}
                  disabled={!editingSubCategory && selectedCategoryId}
                >
                  <option value="">Select Category</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.categoryName}</option>
                  ))}
                </select>
              </FormGroup>
              <FormGroup>
                <label>Description</label>
                <textarea
                  value={subCategoryForm.description}
                  onChange={(e) => setSubCategoryForm({ ...subCategoryForm, description: e.target.value })}
                  placeholder="Enter description"
                  rows={3}
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={() => setShowSubCategoryModal(false)}>Cancel</CancelButton>
              {canEdit && (
                <SaveButton onClick={handleSaveSubCategory}>
                  {editingSubCategory ? 'Update' : 'Save'}
                </SaveButton>
              )}
            </ModalFooter>
          </Modal>
        </ModalOverlay>
      )}

      {/* Item Modal */}
      {showBarcodeScanner && (
        <POSCameraScanner
          onDetected={(code) => {
            setItemForm(prev => ({ ...prev, barcode: code }));
            setShowBarcodeScanner(false);
            return { ok: true, message: `Barcode: ${code}` };
          }}
          onClose={() => setShowBarcodeScanner(false)}
        />
      )}

      {showItemModal && (
        <ModalOverlay>
          <ItemModal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>{editingItem ? 'Edit Item' : 'Add Item'}</h3>
              <CloseButton onClick={() => setShowItemModal(false)}>
                <FiX />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <TabStrip>
                <TabButton
                  type="button"
                  $active={itemModalTab === 'basic'}
                  onClick={() => setItemModalTab('basic')}
                >
                  Basic
                </TabButton>
                <TabButton
                  type="button"
                  $active={itemModalTab === 'advanced'}
                  onClick={() => setItemModalTab('advanced')}
                >
                  Advanced
                </TabButton>
                {multiUnitEnabled && (
                  <TabButton
                    type="button"
                    $active={itemModalTab === 'units'}
                    onClick={() => setItemModalTab('units')}
                  >
                    Units & Pricing
                  </TabButton>
                )}
              </TabStrip>

              {itemModalTab === 'basic' && (
                <FormSection>
                  <FormRow>
                    <FormGroup>
                      <label>Item Code</label>
                      <input
                        type="text"
                        value={itemForm.itemCode}
                        onChange={(e) => setItemForm({ ...itemForm, itemCode: e.target.value })}
                        disabled={editingItem}
                      />
                    </FormGroup>
                    <FormGroup>
                      <label>Unit of Measure</label>
                      <input
                        type="text"
                        list="uom-suggestions"
                        value={itemForm.unitOfMeasure}
                        onChange={(e) => setItemForm({ ...itemForm, unitOfMeasure: e.target.value })}
                        placeholder="Pcs, Kg, Mtr, Yard, ..."
                        disabled={itemForm.hasSize}
                        title={itemForm.hasSize ? 'Locked to SQft because Has Size is on' : undefined}
                      />
                      <datalist id="uom-suggestions">
                        {(() => {
                          const fromBank = unitsBank.map(u => u.unitCode).filter(Boolean);
                          const set = new Set([...fromBank, ...unitOptions]);
                          return Array.from(set).map(u => <option key={u} value={u} />);
                        })()}
                      </datalist>
                    </FormGroup>
                  </FormRow>

                  {/* ── Sold by weight (POS weight-scale plugin) ── */}
                  {isModuleEnabled('pos_weight_scale') && !itemForm.hasSize && (
                    <div style={{ marginTop: 8, padding: 12, background: '#FAFBFC', border: '1px solid #E9ECEF', borderRadius: 6 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                        <input
                          type="checkbox"
                          checked={!!itemForm.soldByWeight}
                          onChange={(e) => setItemForm(prev => ({
                            ...prev,
                            soldByWeight: e.target.checked,
                            unitOfMeasure: e.target.checked ? 'Kg' : (prev.unitOfMeasure === 'Kg' ? 'Pcs' : prev.unitOfMeasure)
                          }))}
                        />
                        Sold by weight — price per Kg, quantity captured from the scale at POS
                      </label>
                    </div>
                  )}

                  {/* ── Has Size (dimensional / area-based inventory) ── */}
                  {isModuleEnabled('item_has_size') && (
                    <div style={{ marginTop: 8, padding: 12, background: '#FAFBFC', border: '1px solid #E9ECEF', borderRadius: 6 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                        <input
                          type="checkbox"
                          checked={!!itemForm.hasSize}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setItemForm({
                              ...itemForm,
                              hasSize: checked,
                              // Force UoM to SQft when turning on; don't change it back when turning off.
                              unitOfMeasure: checked ? 'SQft' : itemForm.unitOfMeasure,
                            });
                          }}
                        />
                        Has Size (Length × Height per piece)
                      </label>
                      {itemForm.hasSize && (
                        <>
                          <div style={{ fontSize: 11, color: '#6c757d', marginTop: 6, marginBottom: 10 }}>
                            Sale &amp; purchase lines for this item accept L × H × Thickness + Pieces and auto-compute SQft. Stock is tracked in SQft.
                          </div>
                          <FormRow>
                            <FormGroup>
                              <label>Size Unit</label>
                              <select
                                value={itemForm.sizeUnit || 'inches'}
                                onChange={(e) => setItemForm({ ...itemForm, sizeUnit: e.target.value })}
                              >
                                <option value="inches">Inches (SQft = L×H ÷ 144)</option>
                                <option value="cm">Centimetres (SQft = L×H ÷ 929)</option>
                              </select>
                            </FormGroup>
                            <FormGroup>
                              <label>Default Length (optional)</label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={itemForm.defaultLength || 0}
                                onChange={(e) => setItemForm({ ...itemForm, defaultLength: e.target.value })}
                                placeholder="0"
                              />
                            </FormGroup>
                          </FormRow>
                          <FormRow>
                            <FormGroup>
                              <label>Default Height (optional)</label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={itemForm.defaultHeight || 0}
                                onChange={(e) => setItemForm({ ...itemForm, defaultHeight: e.target.value })}
                                placeholder="0"
                              />
                            </FormGroup>
                            <FormGroup>
                              <label>Default Thickness (optional)</label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={itemForm.defaultThickness || 0}
                                onChange={(e) => setItemForm({ ...itemForm, defaultThickness: e.target.value })}
                                placeholder="0"
                              />
                            </FormGroup>
                          </FormRow>
                        </>
                      )}
                    </div>
                  )}

                  <FormGroup>
                    <label>Item Name *</label>
                    <input
                      type="text"
                      value={itemForm.itemName}
                      onChange={(e) => setItemForm({ ...itemForm, itemName: e.target.value })}
                      placeholder="Enter item name"
                    />
                  </FormGroup>
                  <FormRow>
                    <FormGroup>
                      <label>Sale Price</label>
                      <input
                        type="number"
                        value={itemForm.salePrice}
                        onChange={(e) => setItemForm({ ...itemForm, salePrice: e.target.value })}
                        min="0"
                        step="0.01"
                      />
                    </FormGroup>
                    <FormGroup>
                      <label>Purchase Price</label>
                      <input
                        type="number"
                        value={itemForm.purchasePrice}
                        onChange={(e) => setItemForm({ ...itemForm, purchasePrice: e.target.value })}
                        min="0"
                        step="0.01"
                      />
                    </FormGroup>
                  </FormRow>
                  <FormGroup>
                    <label>Item Image (Optional)</label>
                    <ImageUploadArea>
                      {(imagePreview || itemForm.imageUrl) ? (
                        <ImagePreviewBox>
                          <img src={imagePreview || itemForm.imageUrl} alt="Item" />
                          <RemoveImageBtn onClick={() => { setImageFile(null); setImagePreview(''); setItemForm({ ...itemForm, imageUrl: '' }); }}>
                            <FiX />
                          </RemoveImageBtn>
                        </ImagePreviewBox>
                      ) : (
                        <ImagePlaceholder onClick={() => imageInputRef.current?.click()}>
                          <FiImage size={24} />
                          <span>Click to upload image</span>
                        </ImagePlaceholder>
                      )}
                      <input
                        ref={imageInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            if (file.size > 5 * 1024 * 1024) { alert('Image must be under 5MB'); return; }
                            setImageFile(file);
                            setImagePreview(URL.createObjectURL(file));
                          }
                        }}
                      />
                      {(imagePreview || itemForm.imageUrl) && (
                        <ChangeImageBtn onClick={() => imageInputRef.current?.click()}>Change Image</ChangeImageBtn>
                      )}
                    </ImageUploadArea>
                  </FormGroup>
                </FormSection>
              )}

              {itemModalTab === 'advanced' && (
                <>
                  <FormSection>
                    <SectionTitle>Type</SectionTitle>
                    <FormGroup>
                      <ItemTypeToggle>
                        <ItemTypeOption
                          type="button"
                          $active={itemForm.itemType !== 'service'}
                          onClick={() => setItemForm({ ...itemForm, itemType: 'product' })}
                        >
                          Product (stock)
                        </ItemTypeOption>
                        <ItemTypeOption
                          type="button"
                          $active={itemForm.itemType === 'service'}
                          onClick={() => setItemForm({ ...itemForm, itemType: 'service' })}
                        >
                          Service (no stock)
                        </ItemTypeOption>
                      </ItemTypeToggle>
                    </FormGroup>
                  </FormSection>

                  <FormSection>
                    <SectionTitle>Identifiers</SectionTitle>
                    <FormRow>
                      <FormGroup>
                        <label>Barcode</label>
                        <InputWithAction>
                          <input
                            type="text"
                            value={itemForm.barcode}
                            onChange={(e) => setItemForm({ ...itemForm, barcode: e.target.value })}
                            placeholder="Enter or click Auto"
                          />
                          <AutoBtn
                            type="button"
                            disabled={!activeCompany?.id}
                            title="Generate a unique EAN-13 barcode"
                            onClick={async () => {
                              try {
                                const code = await generateNextBarcode(activeCompany.id);
                                setItemForm(prev => ({ ...prev, barcode: code }));
                              } catch (err) {
                                console.error('Auto barcode failed:', err);
                                alert('Could not generate barcode: ' + (err?.message || 'unknown error'));
                              }
                            }}
                          >
                            AUTO
                          </AutoBtn>
                          <AutoBtn
                            type="button"
                            title="Scan a barcode with the camera"
                            onClick={() => setShowBarcodeScanner(true)}
                            style={{ background: colors.secondary?.main || '#800020' }}
                          >
                            <FiCamera size={14} />
                          </AutoBtn>
                        </InputWithAction>
                      </FormGroup>
                      <FormGroup>
                        <label>HS Code</label>
                        <input
                          type="text"
                          value={itemForm.hsCode}
                          onChange={(e) => setItemForm({ ...itemForm, hsCode: e.target.value })}
                          placeholder="e.g. 8471.30.00"
                        />
                      </FormGroup>
                    </FormRow>
                    <FormGroup>
                      <label>Description</label>
                      <textarea
                        value={itemForm.description}
                        onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                        placeholder="Enter description"
                        rows={2}
                      />
                    </FormGroup>
                  </FormSection>

                  <FormSection>
                    <SectionTitle>Additional Pricing</SectionTitle>
                    <FormRow>
                      <FormGroup>
                        <label>Minimum Price</label>
                        <input
                          type="number"
                          value={itemForm.minimumPrice}
                          onChange={(e) => setItemForm({ ...itemForm, minimumPrice: e.target.value })}
                          min="0"
                          step="0.01"
                        />
                      </FormGroup>
                      <FormGroup>
                        <label>Wholesale Price</label>
                        <input
                          type="number"
                          value={itemForm.wholesalePrice}
                          onChange={(e) => setItemForm({ ...itemForm, wholesalePrice: e.target.value })}
                          min="0"
                          step="0.01"
                        />
                      </FormGroup>
                    </FormRow>
                    <FormRow>
                      <FormGroup>
                        <label>Dealer Price</label>
                        <input
                          type="number"
                          value={itemForm.dealerPrice}
                          onChange={(e) => setItemForm({ ...itemForm, dealerPrice: e.target.value })}
                          min="0"
                          step="0.01"
                        />
                      </FormGroup>
                      <FormGroup>
                        <label>Average Cost</label>
                        <input
                          type="number"
                          value={itemForm.averageCost}
                          onChange={(e) => setItemForm({ ...itemForm, averageCost: e.target.value })}
                          min="0"
                          step="0.01"
                          disabled
                        />
                      </FormGroup>
                    </FormRow>
                    {isModuleEnabled('mrp_pricing') && (
                      <FormRow>
                        <FormGroup>
                          <label>MRP</label>
                          <input
                            type="number"
                            value={itemForm.mrp}
                            onChange={(e) => setItemForm({ ...itemForm, mrp: e.target.value })}
                            min="0"
                            step="0.01"
                            placeholder="Maximum Retail Price per unit"
                          />
                        </FormGroup>
                      </FormRow>
                    )}
                  </FormSection>

                  <FormSection>
                    <SectionTitle>Tax & Inventory</SectionTitle>
                    <FormRow>
                      <FormGroup>
                        <CheckboxGroup>
                          <input
                            type="checkbox"
                            id="taxable"
                            checked={itemForm.taxable}
                            onChange={(e) => setItemForm({ ...itemForm, taxable: e.target.checked })}
                          />
                          <label htmlFor="taxable">Taxable Item</label>
                        </CheckboxGroup>
                      </FormGroup>
                      <FormGroup>
                        <label>{taxLabel} Rate (%)</label>
                        <input
                          type="number"
                          value={itemForm.gstRate}
                          onChange={(e) => setItemForm({ ...itemForm, gstRate: e.target.value })}
                          min="0"
                          max="100"
                          disabled={!itemForm.taxable}
                        />
                      </FormGroup>
                    </FormRow>
                    {thirdScheduleEnabled && (
                      <FormRow>
                        <FormGroup>
                          <label>Sale Tax Type</label>
                          <select
                            value={itemForm.thirdSchedule ? '3rd' : 'normal'}
                            onChange={(e) => setItemForm({ ...itemForm, thirdSchedule: e.target.value === '3rd' })}
                          >
                            <option value="normal">Normal (GST after discount)</option>
                            <option value="3rd">3rd Schedule (GST on full price, before discount)</option>
                          </select>
                        </FormGroup>
                        <FormGroup />
                      </FormRow>
                    )}
                    <FormRow>
                      <FormGroup>
                        <label>Tracking Type</label>
                        <select
                          value={itemForm.itemType === 'service' ? 'None' : itemForm.trackingType}
                          onChange={(e) => setItemForm({ ...itemForm, trackingType: e.target.value })}
                          disabled={itemForm.itemType === 'service'}
                        >
                          <option value="None">None</option>
                          <option value="Batch">Batch Tracking</option>
                          <option value="Serial">Serial Number Tracking</option>
                        </select>
                      </FormGroup>
                      <FormGroup>
                        <label>Re-Order Qty</label>
                        <input
                          type="number"
                          value={itemForm.itemType === 'service' ? 0 : itemForm.reorderQuantity}
                          onChange={(e) => setItemForm({ ...itemForm, reorderQuantity: e.target.value })}
                          min="0"
                          disabled={itemForm.itemType === 'service'}
                        />
                      </FormGroup>
                    </FormRow>
                  </FormSection>
                </>
              )}

              {multiUnitEnabled && itemModalTab === 'units' && (
                <FormSection>
                  <UnitsHelpBox>
                    Define how this item is sold or purchased. Set <strong>one base unit</strong>
                    {' '}(conversion = 1) — usually the smallest unit you sell in (e.g. Pcs).
                    {' '}Add larger packs (Ctn, Box) with their conversion to base
                    {' '}(e.g. 1 Ctn = 24 Pcs). Inventory balances are kept in base units.
                  </UnitsHelpBox>

                  <UnitsTable>
                    <thead>
                      <tr>
                        <UnitTh>Unit</UnitTh>
                        <UnitTh style={{ width: '90px' }}>Base?</UnitTh>
                        <UnitTh style={{ width: '110px' }}>Conversion</UnitTh>
                        <UnitTh>Sale Price</UnitTh>
                        <UnitTh>Purchase Price</UnitTh>
                        <UnitTh>Barcode</UnitTh>
                        <UnitTh style={{ width: '40px' }}></UnitTh>
                      </tr>
                    </thead>
                    <tbody>
                      {(itemForm.units || []).map((u, idx) => (
                        <tr key={idx}>
                          <UnitTd>
                            <select
                              value={u.unitCode || ''}
                              onChange={(e) => {
                                const list = [...itemForm.units];
                                list[idx] = { ...list[idx], unitCode: e.target.value };
                                setItemForm({ ...itemForm, units: list });
                              }}
                            >
                              <option value="">-- Select --</option>
                              {unitsBank.length > 0
                                ? unitsBank.map(ub => (
                                    <option key={ub.id} value={ub.unitCode}>
                                      {ub.unitCode} ({ub.unitName})
                                    </option>
                                  ))
                                : unitOptions.map(uc => (
                                    <option key={uc} value={uc}>{uc}</option>
                                  ))
                              }
                            </select>
                          </UnitTd>
                          <UnitTd style={{ textAlign: 'center' }}>
                            <input
                              type="radio"
                              name="baseUnitRadio"
                              checked={!!u.isBase}
                              onChange={() => {
                                const list = itemForm.units.map((row, i) => ({
                                  ...row,
                                  isBase: i === idx,
                                  conversion: i === idx ? 1 : row.conversion
                                }));
                                setItemForm({ ...itemForm, units: list });
                              }}
                            />
                          </UnitTd>
                          <UnitTd>
                            <input
                              type="number"
                              min="0"
                              step="0.0001"
                              value={u.conversion ?? 1}
                              disabled={!!u.isBase}
                              onChange={(e) => {
                                const list = [...itemForm.units];
                                list[idx] = { ...list[idx], conversion: e.target.value };
                                setItemForm({ ...itemForm, units: list });
                              }}
                            />
                          </UnitTd>
                          <UnitTd>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={u.salePrice ?? 0}
                              onChange={(e) => {
                                const list = [...itemForm.units];
                                list[idx] = { ...list[idx], salePrice: e.target.value };
                                setItemForm({ ...itemForm, units: list });
                              }}
                            />
                          </UnitTd>
                          <UnitTd>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={u.purchasePrice ?? 0}
                              onChange={(e) => {
                                const list = [...itemForm.units];
                                list[idx] = { ...list[idx], purchasePrice: e.target.value };
                                setItemForm({ ...itemForm, units: list });
                              }}
                            />
                          </UnitTd>
                          <UnitTd>
                            <InputWithAction>
                              <input
                                type="text"
                                value={u.barcode || ''}
                                placeholder="Optional"
                                onChange={(e) => {
                                  const list = [...itemForm.units];
                                  list[idx] = { ...list[idx], barcode: e.target.value };
                                  setItemForm({ ...itemForm, units: list });
                                }}
                              />
                              <MiniAutoBtn
                                type="button"
                                disabled={!activeCompany?.id}
                                title="Generate a unique EAN-13 barcode for this pack size"
                                onClick={async () => {
                                  try {
                                    const code = await generateNextBarcode(activeCompany.id);
                                    const list = [...itemForm.units];
                                    list[idx] = { ...list[idx], barcode: code };
                                    setItemForm({ ...itemForm, units: list });
                                  } catch (err) {
                                    console.error('Auto barcode failed:', err);
                                    alert('Could not generate barcode: ' + (err?.message || 'unknown error'));
                                  }
                                }}
                              >
                                AUTO
                              </MiniAutoBtn>
                            </InputWithAction>
                          </UnitTd>
                          <UnitTd>
                            <RemoveUnitBtn
                              type="button"
                              title="Remove"
                              onClick={() => {
                                const list = itemForm.units.filter((_, i) => i !== idx);
                                setItemForm({ ...itemForm, units: list });
                              }}
                            >
                              <FiX />
                            </RemoveUnitBtn>
                          </UnitTd>
                        </tr>
                      ))}
                    </tbody>
                  </UnitsTable>

                  <AddUnitBtn
                    type="button"
                    onClick={() => {
                      const isFirst = !itemForm.units || itemForm.units.length === 0;
                      const newRow = {
                        unitCode: '',
                        isBase: isFirst,
                        conversion: 1,
                        salePrice: isFirst ? Number(itemForm.salePrice) || 0 : 0,
                        purchasePrice: isFirst ? Number(itemForm.purchasePrice) || 0 : 0,
                        barcode: ''
                      };
                      setItemForm({ ...itemForm, units: [...(itemForm.units || []), newRow] });
                    }}
                  >
                    <FiPlus /> Add Unit Row
                  </AddUnitBtn>

                  {(itemForm.units && itemForm.units.length > 0) && (
                    <FormRow>
                      <FormGroup>
                        <label>Default Purchase Unit{twoUnitsEnabled ? ' (rate per)' : ''}</label>
                        <select
                          value={itemForm.defaultPurchaseUnit || ''}
                          onChange={(e) => setItemForm({ ...itemForm, defaultPurchaseUnit: e.target.value })}
                        >
                          <option value="">(use base)</option>
                          {itemForm.units.filter(u => u.unitCode).map(u => (
                            <option key={u.unitCode} value={u.unitCode}>{u.unitCode}</option>
                          ))}
                        </select>
                      </FormGroup>
                      <FormGroup>
                        <label>Default Sale Unit{twoUnitsEnabled ? ' (rate per)' : ''}</label>
                        <select
                          value={itemForm.defaultSaleUnit || ''}
                          onChange={(e) => setItemForm({ ...itemForm, defaultSaleUnit: e.target.value })}
                        >
                          <option value="">(use base)</option>
                          {itemForm.units.filter(u => u.unitCode).map(u => (
                            <option key={u.unitCode} value={u.unitCode}>{u.unitCode}</option>
                          ))}
                        </select>
                      </FormGroup>
                    </FormRow>
                  )}

                  {/* Show Two Units (module item_two_units): pick two units to show
                      together on sale/purchase lines. Rate is per the line's unit
                      (Default Sale / Purchase Unit above), changeable on each line. */}
                  {twoUnitsEnabled && (itemForm.units || []).filter(u => u.unitCode).length >= 2 && (
                    <div style={{ marginTop: 12, padding: 12, border: '1px dashed #CBD5E1', borderRadius: 8, background: '#F8FAFC' }}>
                      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Show Two Units Together</div>
                      <div style={{ fontSize: 12, color: '#64748B', marginBottom: 8 }}>
                        Sale and purchase lines will show the quantity in both units at the same time. Typing in one fills the other by the conversion above. Price is per the line's unit (Default Sale / Purchase Unit), and can be changed on each line.
                      </div>
                      <FormRow>
                        {[0, 1].map(pos => (
                          <FormGroup key={pos}>
                            <label>{pos === 0 ? 'First Unit' : 'Second Unit'}</label>
                            <select
                              data-show-unit={pos}
                              value={(itemForm.showUnits && itemForm.showUnits[pos]) || ''}
                              onChange={(e) => {
                                const next = [...(Array.isArray(itemForm.showUnits) ? itemForm.showUnits : [])];
                                next[0] = next[0] || ''; next[1] = next[1] || '';
                                next[pos] = e.target.value;
                                setItemForm({ ...itemForm, showUnits: next });
                              }}
                            >
                              <option value="">(none)</option>
                              {itemForm.units.filter(u => u.unitCode).map(u => (
                                <option key={u.unitCode} value={u.unitCode}>{u.unitCode}</option>
                              ))}
                            </select>
                          </FormGroup>
                        ))}
                      </FormRow>
                    </div>
                  )}
                </FormSection>
              )}
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={() => setShowItemModal(false)}>Cancel</CancelButton>
              {canEdit && (
                <SaveButton onClick={handleSaveItem} disabled={uploadingImage}>
                  {uploadingImage ? 'Uploading...' : editingItem ? 'Update' : 'Save'}
                </SaveButton>
              )}
            </ModalFooter>
          </ItemModal>
        </ModalOverlay>
      )}

      {/* Image Import Result — lists every mismatch / error */}
      {imageImportResult && (
        <ModalOverlay onClick={() => setImageImportResult(null)}>
          <ImportModal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3 style={{ margin: 0, fontSize: 16 }}>Image Import Result</h3>
              <CloseButton onClick={() => setImageImportResult(null)}>×</CloseButton>
            </ModalHeader>
            <div style={{ padding: '4px 2px 6px' }}>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 14, fontSize: 14, fontWeight: 600 }}>
                <span style={{ color: '#16a34a' }}>✅ Attached: {imageImportResult.attached}</span>
                <span style={{ color: '#d97706' }}>⚠ Not matched: {imageImportResult.notMatched.length}</span>
                <span style={{ color: '#dc2626' }}>❌ Over 5MB: {imageImportResult.tooLarge.length}</span>
                <span style={{ color: '#b91c1c' }}>❌ Failed: {imageImportResult.failed.length}</span>
              </div>

              {imageImportResult.notMatched.length === 0 && imageImportResult.tooLarge.length === 0 && imageImportResult.failed.length === 0 ? (
                <div style={{ color: '#16a34a', fontSize: 14, padding: '8px 0' }}>All images attached successfully. 🎉</div>
              ) : (
                <>
                  {imageImportResult.notMatched.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#b45309' }}>⚠ No matching Item Code ({imageImportResult.notMatched.length})</div>
                      <div style={{ fontSize: 11.5, color: '#92400e', margin: '2px 0 6px' }}>Rename each file to the exact item code (e.g. ITM-001.jpg), or that item isn't created yet.</div>
                      <div style={{ maxHeight: 130, overflowY: 'auto', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '6px 10px', fontSize: 12, color: '#374151' }}>
                        {imageImportResult.notMatched.map((f, i) => <div key={i}>{f}</div>)}
                      </div>
                    </div>
                  )}
                  {imageImportResult.tooLarge.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#b91c1c' }}>❌ Over 5MB — skipped ({imageImportResult.tooLarge.length})</div>
                      <div style={{ maxHeight: 110, overflowY: 'auto', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '6px 10px', fontSize: 12, color: '#374151', marginTop: 4 }}>
                        {imageImportResult.tooLarge.map((f, i) => <div key={i}>{f}</div>)}
                      </div>
                    </div>
                  )}
                  {imageImportResult.failed.length > 0 && (
                    <div style={{ marginBottom: 4 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#b91c1c' }}>❌ Failed to upload ({imageImportResult.failed.length})</div>
                      <div style={{ maxHeight: 130, overflowY: 'auto', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '6px 10px', fontSize: 12, color: '#374151', marginTop: 4 }}>
                        {imageImportResult.failed.map((f, i) => <div key={i}>{f}</div>)}
                      </div>
                    </div>
                  )}
                </>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <button onClick={() => setImageImportResult(null)} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#0ea5e9', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Done</button>
              </div>
            </div>
          </ImportModal>
        </ModalOverlay>
      )}

      {/* Import Progress Modal */}
      {showImportModal && (
        <ModalOverlay>
          <ImportModal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>Import Inventory Data</h3>
              {!importing && (
                <CloseButton onClick={() => setShowImportModal(false)}>
                  <FiX />
                </CloseButton>
              )}
            </ModalHeader>
            <ModalBody>
              {importing ? (
                <ImportProgressContainer>
                  <ProgressStatus>{importProgress.status}</ProgressStatus>
                  {importProgress.total > 0 && (
                    <>
                      <ProgressBar>
                        <ProgressFill
                          style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                        />
                      </ProgressBar>
                      <ProgressText>
                        {importProgress.current} of {importProgress.total}
                      </ProgressText>
                    </>
                  )}
                </ImportProgressContainer>
              ) : importResults ? (
                <ImportResultsContainer>
                  {importResults.error ? (
                    <ErrorMessage>{importResults.error}</ErrorMessage>
                  ) : (
                    <>
                      <ResultSection>
                        <ResultTitle>Categories</ResultTitle>
                        <ResultStats>
                          <ResultStat $success>
                            <span>{importResults.categories.success}</span> imported
                          </ResultStat>
                          {importResults.categories.failed > 0 && (
                            <ResultStat $error>
                              <span>{importResults.categories.failed}</span> failed
                            </ResultStat>
                          )}
                        </ResultStats>
                        {importResults.categories.errors.length > 0 && (
                          <ErrorList>
                            {importResults.categories.errors.slice(0, 5).map((err, i) => (
                              <ErrorItem key={i}>{err}</ErrorItem>
                            ))}
                            {importResults.categories.errors.length > 5 && (
                              <ErrorItem>...and {importResults.categories.errors.length - 5} more errors</ErrorItem>
                            )}
                          </ErrorList>
                        )}
                      </ResultSection>

                      <ResultSection>
                        <ResultTitle>Sub-Categories</ResultTitle>
                        <ResultStats>
                          <ResultStat $success>
                            <span>{importResults.subCategories.success}</span> imported
                          </ResultStat>
                          {importResults.subCategories.failed > 0 && (
                            <ResultStat $error>
                              <span>{importResults.subCategories.failed}</span> failed
                            </ResultStat>
                          )}
                        </ResultStats>
                        {importResults.subCategories.errors.length > 0 && (
                          <ErrorList>
                            {importResults.subCategories.errors.slice(0, 5).map((err, i) => (
                              <ErrorItem key={i}>{err}</ErrorItem>
                            ))}
                            {importResults.subCategories.errors.length > 5 && (
                              <ErrorItem>...and {importResults.subCategories.errors.length - 5} more errors</ErrorItem>
                            )}
                          </ErrorList>
                        )}
                      </ResultSection>

                      <ResultSection>
                        <ResultTitle>Items</ResultTitle>
                        <ResultStats>
                          <ResultStat $success>
                            <span>{importResults.items.success}</span> imported
                          </ResultStat>
                          {importResults.items.failed > 0 && (
                            <ResultStat $error>
                              <span>{importResults.items.failed}</span> failed
                            </ResultStat>
                          )}
                        </ResultStats>
                        {importResults.items.errors.length > 0 && (
                          <ErrorList>
                            {importResults.items.errors.slice(0, 5).map((err, i) => (
                              <ErrorItem key={i}>{err}</ErrorItem>
                            ))}
                            {importResults.items.errors.length > 5 && (
                              <ErrorItem>...and {importResults.items.errors.length - 5} more errors</ErrorItem>
                            )}
                          </ErrorList>
                        )}
                      </ResultSection>

                      <TotalSummary>
                        Total: {importResults.categories.success + importResults.subCategories.success + importResults.items.success} items imported successfully
                      </TotalSummary>
                    </>
                  )}
                </ImportResultsContainer>
              ) : null}
            </ModalBody>
            {!importing && (
              <ModalFooter>
                <SaveButton onClick={() => setShowImportModal(false)}>Close</SaveButton>
              </ModalFooter>
            )}
          </ImportModal>
        </ModalOverlay>
      )}

      {showCatalogueModal && (
        <ModalOverlay>
          <ItemModal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>Print Item Catalogue</h3>
              <CloseButton onClick={() => setShowCatalogueModal(false)}><FiX /></CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormSection>
                <SectionTitle>Design</SectionTitle>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: spacing.sm }}>
                  {CATALOGUE_DESIGNS.map(d => (
                    <div
                      key={d.id}
                      onClick={() => setCatalogueOptions(prev => ({ ...prev, design: d.id }))}
                      style={{
                        padding: spacing.md,
                        border: `2px solid ${catalogueOptions.design === d.id ? colors.primary.main : colors.border.light}`,
                        borderRadius: borderRadius.md,
                        cursor: 'pointer',
                        background: catalogueOptions.design === d.id ? `${colors.primary.main}10` : colors.background.default,
                      }}
                    >
                      <div style={{ fontWeight: typography.fontWeight.semibold, fontSize: typography.fontSize.sm, marginBottom: 4 }}>{d.name}</div>
                      <div style={{ fontSize: typography.fontSize.xs, color: colors.text.secondary, lineHeight: 1.3 }}>{d.desc}</div>
                    </div>
                  ))}
                </div>
              </FormSection>

              <FormSection>
                <SectionTitle>Filter</SectionTitle>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.sm }}>
                  <div>
                    <label style={{ fontSize: typography.fontSize.xs, color: colors.text.secondary, display: 'block', marginBottom: 4 }}>Category</label>
                    <select
                      value={catalogueOptions.categoryFilter}
                      onChange={(e) => setCatalogueOptions(prev => ({ ...prev, categoryFilter: e.target.value, subCategoryFilter: 'all' }))}
                      style={{ width: '100%', padding: spacing.sm, border: `1px solid ${colors.border.main}`, borderRadius: borderRadius.sm, fontSize: typography.fontSize.sm }}
                    >
                      <option value="all">All Categories</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.categoryName}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: typography.fontSize.xs, color: colors.text.secondary, display: 'block', marginBottom: 4 }}>Sub-Category</label>
                    <select
                      value={catalogueOptions.subCategoryFilter}
                      onChange={(e) => setCatalogueOptions(prev => ({ ...prev, subCategoryFilter: e.target.value }))}
                      disabled={catalogueOptions.categoryFilter === 'all'}
                      style={{ width: '100%', padding: spacing.sm, border: `1px solid ${colors.border.main}`, borderRadius: borderRadius.sm, fontSize: typography.fontSize.sm }}
                    >
                      <option value="all">All Sub-Categories</option>
                      {subCategories.filter(s => s.categoryId === catalogueOptions.categoryFilter).map(s => (
                        <option key={s.id} value={s.id}>{s.subCategoryName}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </FormSection>

              <FormSection>
                <SectionTitle>Columns / Fields</SectionTitle>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px' }}>
                  {CATALOGUE_COLUMNS.map(col => {
                    const isOn = col.key in catalogueOptions.columns
                      ? !!catalogueOptions.columns[col.key]
                      : defaultColumnOn(col.key);
                    return (
                      <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: typography.fontSize.sm, cursor: 'pointer' }}>
                        <input type="checkbox" checked={isOn} onChange={() => toggleCatalogueColumn(col.key)} />
                        {col.label}
                      </label>
                    );
                  })}
                </div>
              </FormSection>
            </ModalBody>
            <ModalFooter>
              <button
                onClick={() => setShowCatalogueModal(false)}
                style={{ padding: `${spacing.sm} ${spacing.md}`, background: colors.background.default, border: `1px solid ${colors.border.main}`, borderRadius: borderRadius.md, fontSize: typography.fontSize.sm, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <SaveButton onClick={handlePrintCatalogue}>
                <FiPrinter /> Print
              </SaveButton>
            </ModalFooter>
          </ItemModal>
        </ModalOverlay>
      )}
    </PageLayout>
  );
};

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.md};
  flex-wrap: wrap;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  background: ${colors.background.default};
  border: 1px solid ${colors.border.main};
  border-radius: ${borderRadius.md};
  padding: ${spacing.sm} ${spacing.md};
  flex: 1;
  max-width: 400px;

  svg {
    color: ${colors.text.secondary};
  }

  input {
    border: none;
    outline: none;
    flex: 1;
    font-size: ${typography.fontSize.sm};
    background: transparent;
  }
`;

const ClearButton = styled.button`
  background: transparent;
  color: ${colors.text.secondary};
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${borderRadius.sm};

  &:hover {
    background: ${colors.background.hover};
  }
`;

const SearchResultsInfo = styled.span`
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.secondary};
  white-space: nowrap;
  padding: 0 ${spacing.sm};
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  flex-wrap: wrap;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};
  background: ${colors.primary.main};
  color: ${colors.text.white};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.primary.dark};
  }
`;

const TemplateButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};
  background: ${colors.success.main};
  color: ${colors.text.white};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.success.dark};
  }
`;

const ImportButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};
  background: ${colors.info.main};
  color: ${colors.text.white};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.info.dark};
  }
`;

const CatalogueButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};
  background: ${colors.warning.main};
  color: ${colors.text.white};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${colors.warning.dark};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${spacing.md};
`;

const StatCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  background: ${colors.background.default};
  padding: ${spacing.md};
  border-radius: ${borderRadius.md};
  border: 1px solid ${colors.border.light};
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${borderRadius.md};
  background: ${props => props.$color}15;
  color: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 24px;
  }
`;

const StatInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatValue = styled.span`
  font-size: ${typography.fontSize.h4};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.text.primary};
`;

const StatLabel = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.secondary};
`;

const TreeContainer = styled.div`
  background: ${colors.background.default};
  border-radius: ${borderRadius.md};
  border: 1px solid ${colors.border.light};
  overflow: hidden;
`;

const ActionDropdown = styled.div`
  position: fixed;
  min-width: 220px;
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(0,0,0,0.18);
  z-index: 9999;
  padding: 4px;
  overflow: hidden;
`;
const ActionDropItem = styled.button`
  display: flex; align-items: center; gap: 8px;
  width: 100%;
  padding: 8px 12px;
  background: none; border: none; text-align: left;
  font-size: 13px; cursor: pointer;
  color: ${p => p.$danger ? colors.error.main : colors.text.primary};
  border-radius: 6px;
  &:hover { background: ${p => p.$danger ? `${colors.error.main}10` : colors.neutral[50]}; }
  svg { flex-shrink: 0; opacity: 0.75; }
`;
const ActionDropDivider = styled.div`
  height: 1px; background: ${colors.neutral[200]}; margin: 4px 0;
`;

// ─── View toggle ────────────────────────────────────────────────────────────
const ViewToggle = styled.div`
  display: inline-flex;
  border: 1px solid ${colors.border.main};
  border-radius: ${borderRadius.md};
  overflow: hidden;
  background: white;
`;
const ViewToggleBtn = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 14px;
  background: ${p => p.$active ? colors.primary.main : 'white'};
  color: ${p => p.$active ? 'white' : colors.text.secondary};
  border: none; cursor: pointer;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  transition: all 0.15s;
  &:not(:last-child) { border-right: 1px solid ${colors.border.main}; }
  &:hover:not(:disabled) {
    background: ${p => p.$active ? colors.primary.dark : colors.neutral[50]};
  }
`;

// ─── Simple 3-panel view ────────────────────────────────────────────────────
const SimpleLayout = styled.div`
  display: flex;
  gap: 12px;
  align-items: stretch;
  min-height: 420px;
  @media (max-width: 1100px) { flex-direction: column; }
`;
const SimplePanel = styled.div`
  flex: 1;
  background: white;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  display: flex; flex-direction: column;
  min-width: 0;
`;
const SimplePanelHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid ${colors.border.light};
  background: ${colors.neutral[50]};
  font-size: 12px;
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;
const SimpleCrumb = styled.span`
  text-transform: none;
  color: ${colors.text.tertiary};
  font-weight: 400;
  letter-spacing: 0;
  font-size: 11px;
`;
const SimpleList = styled.div`
  flex: 1;
  overflow-y: auto;
  max-height: 480px;
`;
const SimpleRow = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  gap: 8px;
  padding: 8px 14px;
  cursor: pointer;
  border-left: 3px solid ${p => p.$active ? colors.primary.main : 'transparent'};
  background: ${p => p.$active ? `${colors.primary.main}0D` : 'white'};
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.12s;
  &:hover { background: ${p => p.$active ? `${colors.primary.main}15` : colors.neutral[50]}; }
  font-size: 13px;
  svg { color: ${colors.text.secondary}; flex-shrink: 0; }
`;
const SimpleRowLeft = styled.div`
  display: flex; align-items: center; gap: 8px;
  min-width: 0; flex: 1;
  > span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
`;
const SimpleRowRight = styled.div`
  display: flex; align-items: center; gap: 6px;
`;
const SimpleCode = styled.span`
  font-family: 'Consolas', 'SF Mono', monospace;
  font-size: 11px;
  color: ${colors.text.tertiary};
`;
const SimpleCount = styled.span`
  font-size: 11px;
  font-family: 'Consolas', 'SF Mono', monospace;
  color: ${colors.text.secondary};
  background: ${colors.neutral[100]};
  padding: 1px 8px;
  border-radius: 10px;
`;
const SimpleActions = styled.div`
  display: none;
  gap: 4px;
  ${SimpleRow}:hover & { display: inline-flex; }
`;
const SimpleAddBtn = styled.button`
  display: flex; align-items: center; justify-content: center; gap: 6px;
  padding: 10px 14px;
  background: ${colors.primary.main}10;
  color: ${colors.primary.main};
  border: none; border-top: 1px solid ${colors.border.light};
  cursor: pointer; font-size: 13px;
  font-weight: ${typography.fontWeight.medium};
  &:hover { background: ${colors.primary.main}20; }
`;
const SimpleAddBtnMuted = styled.div`
  padding: 10px 14px;
  text-align: center;
  font-size: 12px;
  color: ${colors.text.tertiary};
  border-top: 1px solid ${colors.border.light};
  background: ${colors.neutral[50]};
`;
const SimpleTableWrap = styled.div`
  flex: 1;
  overflow: auto;
  max-height: 540px;
`;
const SimpleTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  thead th {
    position: sticky; top: 0;
    background: ${colors.neutral[50]};
    border-bottom: 2px solid ${colors.neutral[200]};
    padding: 10px 12px;
    text-align: left;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: ${colors.text.secondary};
  }
  tbody td { padding: 8px 12px; border-bottom: 1px solid ${colors.neutral[100]}; }
  tbody tr:hover td { background: ${colors.neutral[50]}; }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${spacing.xl};
  color: ${colors.text.secondary};
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: ${spacing.xl};
  color: ${colors.text.secondary};
`;

const CategoryNode = styled.div`
  border-bottom: 1px solid ${colors.border.light};

  &:last-child {
    border-bottom: none;
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing.md};
  background: ${props => props.$isMatch ? colors.warning.main + '15' : colors.background.default};
  transition: background 0.2s ease;
  border-left: ${props => props.$isMatch ? `4px solid ${colors.warning.main}` : 'none'};

  &:hover {
    background: ${props => props.$isMatch ? colors.warning.main + '25' : colors.background.hover};
  }
`;

const CategoryLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  cursor: pointer;
  flex: 1;

  > svg:first-child {
    color: ${colors.text.secondary};
  }

  > svg:nth-child(2) {
    color: ${colors.primary.main};
  }
`;

const CategoryCode = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${props => props.$isMatch ? colors.warning.dark : colors.text.secondary};
  font-family: monospace;
  background: ${props => props.$isMatch ? colors.warning.main + '30' : colors.background.paper};
  padding: 2px 6px;
  border-radius: ${borderRadius.sm};
  font-weight: ${props => props.$isMatch ? typography.fontWeight.semibold : 'normal'};
`;

const CategoryName = styled.span`
  font-weight: ${props => props.$isMatch ? typography.fontWeight.bold : typography.fontWeight.medium};
  color: ${props => props.$isMatch ? colors.warning.dark : colors.text.primary};
`;

const CategoryCount = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.secondary};
`;

const CategoryActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: ${props => props.$danger ? 'transparent' : colors.primary.main + '12'};
  color: ${props => props.$danger ? colors.error.main : colors.primary.main};
  border: 1px solid ${props => props.$danger ? 'transparent' : colors.primary.main + '25'};
  border-radius: ${borderRadius.sm};
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$danger ? colors.error.main + '15' : colors.primary.main + '22'};
    color: ${props => props.$danger ? colors.error.main : colors.primary.dark || colors.primary.main};
  }
`;

const SubCategoryList = styled.div`
  margin-left: ${spacing.lg};
  border-left: 2px solid ${colors.border.light};
`;

const SubCategoryNode = styled.div`
  border-bottom: 1px solid ${colors.border.light};

  &:last-child {
    border-bottom: none;
  }
`;

const SubCategoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing.sm} ${spacing.md};
  background: ${props => props.$isMatch ? colors.warning.main + '15' : colors.background.paper};
  transition: background 0.2s ease;
  border-left: ${props => props.$isMatch ? `3px solid ${colors.warning.main}` : 'none'};

  &:hover {
    background: ${props => props.$isMatch ? colors.warning.main + '25' : colors.background.hover};
  }
`;

const SubCategoryLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  cursor: pointer;
  flex: 1;

  > svg:first-child {
    color: ${colors.text.secondary};
  }

  > svg:nth-child(2) {
    color: ${colors.success.main};
  }
`;

const SubCategoryCode = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${props => props.$isMatch ? colors.warning.dark : colors.text.secondary};
  font-family: monospace;
  background: ${props => props.$isMatch ? colors.warning.main + '30' : colors.background.default};
  padding: 2px 6px;
  border-radius: ${borderRadius.sm};
  font-weight: ${props => props.$isMatch ? typography.fontWeight.semibold : 'normal'};
`;

const SubCategoryName = styled.span`
  font-weight: ${props => props.$isMatch ? typography.fontWeight.bold : typography.fontWeight.medium};
  color: ${props => props.$isMatch ? colors.warning.dark : colors.text.primary};
  font-size: ${typography.fontSize.sm};
`;

const SubCategoryCount = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.secondary};
`;

const EmptySubMessage = styled.div`
  padding: ${spacing.md};
  text-align: center;
  color: ${colors.text.secondary};
  font-size: ${typography.fontSize.sm};
`;

const ItemList = styled.div`
  margin-left: ${spacing.lg};
  border-left: 2px solid ${colors.border.light};
  background: ${colors.background.default};
`;

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing.sm} ${spacing.md};
  border-bottom: 1px solid ${colors.border.light};
  transition: background 0.2s ease;
  background: ${props => props.$isMatch ? colors.warning.main + '15' : 'transparent'};
  border-left: ${props => props.$isMatch ? `3px solid ${colors.warning.main}` : 'none'};

  &:hover {
    background: ${props => props.$isMatch ? colors.warning.main + '25' : colors.background.hover};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const ItemLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  flex: 1;

  > svg {
    color: ${colors.info.main};
  }
`;

const ItemCode = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${props => props.$isMatch ? colors.warning.dark : colors.text.secondary};
  font-family: monospace;
  background: ${props => props.$isMatch ? colors.warning.main + '30' : colors.background.paper};
  padding: 2px 6px;
  border-radius: ${borderRadius.sm};
  font-weight: ${props => props.$isMatch ? typography.fontWeight.semibold : 'normal'};
`;

const ItemName = styled.span`
  color: ${props => props.$isMatch ? colors.warning.dark : colors.text.primary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${props => props.$isMatch ? typography.fontWeight.semibold : 'normal'};
`;

const ItemBarcode = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${colors.warning.dark};
  font-family: monospace;
  background: ${colors.warning.main}30;
  padding: 2px 6px;
  border-radius: ${borderRadius.sm};
  font-weight: ${typography.fontWeight.medium};
`;

const ItemUnit = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.secondary};
  background: ${colors.background.paper};
  padding: 2px 8px;
  border-radius: ${borderRadius.sm};
`;

const ItemPrice = styled.span`
  font-size: ${typography.fontSize.sm};
  color: ${colors.success.main};
  font-weight: ${typography.fontWeight.medium};
`;

const ItemStock = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${props => props.$low ? colors.error.main : colors.text.secondary};
  background: ${props => props.$low ? colors.error.main + '15' : colors.background.paper};
  padding: 2px 8px;
  border-radius: ${borderRadius.sm};
`;

const EmptyItemMessage = styled.div`
  padding: ${spacing.sm} ${spacing.md};
  text-align: center;
  color: ${colors.text.secondary};
  font-size: ${typography.fontSize.xs};
  font-style: italic;
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${spacing.md};
`;

const Modal = styled.div`
  background: ${colors.background.default};
  border-radius: ${borderRadius.lg};
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${shadows.xl};
`;

const ItemModal = styled(Modal)`
  max-width: 700px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing.md} ${spacing.lg};
  border-bottom: 1px solid ${colors.border.light};

  h3 {
    font-size: ${typography.fontSize.lg};
    font-weight: ${typography.fontWeight.semibold};
    color: ${colors.text.primary};
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  color: ${colors.text.secondary};
  border-radius: ${borderRadius.sm};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.background.hover};
    color: ${colors.text.primary};
  }
`;

const ModalBody = styled.div`
  padding: ${spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`;

const TabStrip = styled.div`
  display: flex;
  gap: ${spacing.xs};
  border-bottom: 1px solid ${colors.border.light};
  margin-bottom: ${spacing.sm};
`;

const TabButton = styled.button`
  padding: ${spacing.sm} ${spacing.md};
  background: transparent;
  border: none;
  border-bottom: 2px solid ${({ $active }) => ($active ? colors.primary.main : 'transparent')};
  color: ${({ $active }) => ($active ? colors.primary.main : colors.text.secondary)};
  font-size: ${typography.fontSize.sm};
  font-weight: ${({ $active }) => ($active ? typography.fontWeight.semibold : typography.fontWeight.medium)};
  cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease;
  margin-bottom: -1px;

  &:hover {
    color: ${colors.primary.main};
  }
`;

const ItemTypeToggle = styled.div`
  display: inline-flex;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.md};
  overflow: hidden;
`;

const ItemTypeOption = styled.button`
  padding: ${spacing.xs} ${spacing.md};
  background: ${({ $active }) => ($active ? colors.primary.main : 'white')};
  color: ${({ $active }) => ($active ? 'white' : colors.text.primary)};
  border: none;
  font-size: ${typography.fontSize.sm};
  font-weight: ${({ $active }) => ($active ? typography.fontWeight.semibold : typography.fontWeight.regular)};
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
  & + & { border-left: 1px solid ${colors.border.light}; }
`;

const UnitsHelpBox = styled.div`
  padding: ${spacing.sm} ${spacing.md};
  background: ${colors.primary.main}08;
  border-left: 3px solid ${colors.primary.main};
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.secondary};
  line-height: 1.5;

  strong { color: ${colors.text.primary}; }
`;

const UnitsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${typography.fontSize.sm};
`;

const UnitTh = styled.th`
  text-align: left;
  padding: ${spacing.xs} ${spacing.sm};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.text.secondary};
  text-transform: uppercase;
  border-bottom: 1px solid ${colors.border.light};
`;

const UnitTd = styled.td`
  padding: ${spacing.xs} ${spacing.sm};
  vertical-align: middle;

  input, select {
    width: 100%;
    padding: 6px 8px;
    border: 1px solid ${colors.border.main};
    border-radius: ${borderRadius.sm};
    font-size: ${typography.fontSize.sm};
    background: ${colors.background.default};

    &:focus { outline: none; border-color: ${colors.primary.main}; }
    &:disabled { background: ${colors.background.paper}; }
  }
`;

const RemoveUnitBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  color: ${colors.error.main};
  border-radius: ${borderRadius.sm};
  &:hover { background: ${colors.error.main}15; }
`;

const AddUnitBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: 6px ${spacing.md};
  background: transparent;
  color: ${colors.primary.main};
  border: 1px dashed ${colors.primary.main};
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  align-self: flex-start;
  cursor: pointer;

  &:hover { background: ${colors.primary.main}10; }
`;

const InputWithAction = styled.div`
  display: flex;
  align-items: stretch;
  gap: 6px;
  & > input { flex: 1; }
`;

const AutoBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 0 12px;
  background: ${colors.primary.main}10;
  color: ${colors.primary.main};
  border: 1px solid ${colors.primary.main}40;
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  letter-spacing: 0.5px;
  cursor: pointer;
  white-space: nowrap;
  &:hover:not(:disabled) {
    background: ${colors.primary.main};
    color: white;
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const MiniAutoBtn = styled.button`
  padding: 0 8px;
  height: 100%;
  min-height: 28px;
  background: ${colors.primary.main}10;
  color: ${colors.primary.main};
  border: 1px solid ${colors.primary.main}40;
  border-radius: ${borderRadius.sm};
  font-size: 10px;
  font-weight: ${typography.fontWeight.semibold};
  cursor: pointer;
  white-space: nowrap;
  &:hover:not(:disabled) {
    background: ${colors.primary.main};
    color: white;
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SectionTitle = styled.h4`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.text.primary};
  padding-bottom: ${spacing.xs};
  border-bottom: 1px solid ${colors.border.light};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${spacing.md};

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};

  label {
    font-size: ${typography.fontSize.sm};
    font-weight: ${typography.fontWeight.medium};
    color: ${colors.text.secondary};
  }

  input, select, textarea {
    padding: ${spacing.sm} ${spacing.md};
    border: 1px solid ${colors.border.main};
    border-radius: ${borderRadius.md};
    font-size: ${typography.fontSize.sm};
    color: ${colors.text.primary};
    background: ${colors.background.default};
    transition: all 0.2s ease;

    &:focus {
      outline: none;
      border-color: ${colors.primary.main};
      box-shadow: 0 0 0 3px ${colors.primary.main}20;
    }

    &:disabled {
      background: ${colors.background.paper};
      cursor: not-allowed;
    }
  }

  textarea {
    resize: vertical;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  padding-top: ${spacing.md};

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }

  label {
    cursor: pointer;
    font-weight: ${typography.fontWeight.regular};
    color: ${colors.text.primary};
  }
`;

const ImageUploadArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.sm};
`;

const ImagePlaceholder = styled.div`
  width: 120px;
  height: 120px;
  border: 2px dashed ${colors.border.main};
  border-radius: ${borderRadius.md};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xs};
  cursor: pointer;
  color: ${colors.text.secondary};
  transition: all 0.2s ease;

  span {
    font-size: ${typography.fontSize.xs};
  }

  &:hover {
    border-color: ${colors.primary.main};
    color: ${colors.primary.main};
    background: ${colors.primary.main}08;
  }
`;

const ImagePreviewBox = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: ${borderRadius.md};
  overflow: hidden;
  border: 1px solid ${colors.border.light};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RemoveImageBtn = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${colors.error.main};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;

  svg { font-size: 12px; }
`;

const ChangeImageBtn = styled.button`
  font-size: ${typography.fontSize.xs};
  color: ${colors.primary.main};
  background: transparent;
  padding: 2px 8px;
  border: 1px solid ${colors.primary.main};
  border-radius: ${borderRadius.sm};
  cursor: pointer;

  &:hover {
    background: ${colors.primary.main}10;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${spacing.sm};
  padding: ${spacing.md} ${spacing.lg};
  border-top: 1px solid ${colors.border.light};
`;

const CancelButton = styled.button`
  padding: ${spacing.sm} ${spacing.lg};
  background: transparent;
  color: ${colors.text.secondary};
  border: 1px solid ${colors.border.main};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.background.hover};
    border-color: ${colors.text.secondary};
  }
`;

const SaveButton = styled.button`
  padding: ${spacing.sm} ${spacing.lg};
  background: ${colors.primary.main};
  color: ${colors.text.white};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.primary.dark};
  }
`;

// Import Modal Styles
const ImportModal = styled(Modal)`
  max-width: 550px;
`;

const ImportProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.md};
  padding: ${spacing.lg};
`;

const ProgressStatus = styled.div`
  font-size: ${typography.fontSize.md};
  color: ${colors.text.primary};
  font-weight: ${typography.fontWeight.medium};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: ${colors.background.paper};
  border-radius: ${borderRadius.md};
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${colors.primary.main};
  border-radius: ${borderRadius.md};
  transition: width 0.3s ease;
`;

const ProgressText = styled.div`
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.secondary};
`;

const ImportResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`;

const ResultSection = styled.div`
  background: ${colors.background.paper};
  border-radius: ${borderRadius.md};
  padding: ${spacing.md};
`;

const ResultTitle = styled.h4`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.text.primary};
  margin-bottom: ${spacing.sm};
`;

const ResultStats = styled.div`
  display: flex;
  gap: ${spacing.md};
`;

const ResultStat = styled.div`
  font-size: ${typography.fontSize.sm};
  color: ${props => props.$success ? colors.success.main : props.$error ? colors.error.main : colors.text.secondary};

  span {
    font-weight: ${typography.fontWeight.bold};
    font-size: ${typography.fontSize.lg};
  }
`;

const ErrorList = styled.div`
  margin-top: ${spacing.sm};
  padding-top: ${spacing.sm};
  border-top: 1px solid ${colors.border.light};
`;

const ErrorItem = styled.div`
  font-size: ${typography.fontSize.xs};
  color: ${colors.error.main};
  padding: 2px 0;
`;

const ErrorMessage = styled.div`
  color: ${colors.error.main};
  font-size: ${typography.fontSize.sm};
  padding: ${spacing.md};
  background: ${colors.error.main}15;
  border-radius: ${borderRadius.md};
`;

const TotalSummary = styled.div`
  font-size: ${typography.fontSize.md};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.success.main};
  text-align: center;
  padding: ${spacing.md};
  background: ${colors.success.main}15;
  border-radius: ${borderRadius.md};
`;

export default ChartOfInventory;
